import type { CSSProperties } from 'react';
import ReactSelect, { MultiValue } from 'react-select';
import CustomerSearchSelect from 'components/customers/CustomerSearchSelect';
import { useHistory } from 'react-router-dom';
import { buildMapsUrl, validateAddress, validateEmail, validateItalianTaxCode, validateItalianVAT } from 'utils/common-utils';
import { useState, useEffect, useRef } from 'react';
import {
  Box, Button, Checkbox, Flex, FormControl, FormErrorMessage, FormHelperText, FormLabel, HStack, Icon,
  IconButton, Input, Link, Menu, MenuButton, MenuItem, MenuList, Modal, ModalBody, ModalCloseButton, ModalContent, ModalFooter, ModalHeader,
  ModalOverlay, Radio, RadioGroup, Select, Spinner, Switch, Table,
  Tbody, Td, Th, Thead, Tfoot, Tr, Textarea, Text, VStack, Badge, AlertDialog, AlertDialogBody,
  AlertDialogContent, AlertDialogFooter, AlertDialogHeader, AlertDialogOverlay,
  useDisclosure, useColorModeValue, Tooltip, useBreakpointValue,
} from '@chakra-ui/react';
import { ChevronDownIcon } from '@chakra-ui/icons';
import { useKeycloak } from '@react-keycloak/web';
import { toast } from 'react-toastify';
import {
  MdOutlineDeleteOutline, MdOutlineEdit,
  MdInfo, MdContentCopy, MdClose,
  MdPerson, MdApartment,
  MdZoomIn, MdZoomOut, MdAdd, MdMoreVert,
  MdChevronLeft, MdChevronRight,
} from 'react-icons/md';
import { FaSync } from 'react-icons/fa';
import * as TaskService from 'services/TaskService';
import * as CustomersService from 'services/CustomersService';
import * as EmployeeService from 'services/EmployeeService';
import * as WeeklyScheduleService from 'services/WeeklyScheduleService';
import * as OperationsTipologyService from 'services/OperationsTipologyService';
import * as VehicleService from 'services/VehicleService';
import {
  Task, TaskStatistics, CreateTaskRequest,
  Customer, Employee, TaskStatus, DeleteMode, OperationsTipology, Vehicle,
} from 'utils/interfaces';
import { hasRole } from 'utils/auth-utils';
import {
  RECURRENCE_TYPE_LABELS, TASK_STATUS_LABELS, DELETE_MODE_LABELS,
} from 'constants/taskLabels';

// ═══════════════════════════════════════════════════════════════════════════════
// WEEKLY GRID — Types, config, helpers
// ═══════════════════════════════════════════════════════════════════════════════

interface ModalState {
  row: 'notes';
  dayIndex: number;
}

interface RowConfig {
  key: 'reperibili' | 'activities' | 'notes' | 'ferie';
  label: string;
  fullLabel: string;
  color: string;
  borderColor: string;
  bgCell: string;
  hoverBg: string;
  borderCell: string;
  minHeight?: string;
  maxHeight?: string;
  textColor: string;
  fontWeight?: number;
  emptyText?: string;
  editBadgeColor: string;
}

const DAYS_IT = ['Lunedì', 'Martedì', 'Mercoledì', 'Giovedì', 'Venerdì', 'Sabato', 'Domenica'];
const DAYS_SHORT = ['LUN', 'MAR', 'MER', 'GIO', 'VEN', 'SAB', 'DOM'];
const MONTHS_IT = ['Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno', 'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre'];

const ROWS: RowConfig[] = [
  {
    key: 'reperibili',
    label: 'REP',
    fullLabel: 'Reperibili',
    color: '#f59e0b',
    borderColor: '#d97706',
    bgCell: '#fffbeb',
    hoverBg: '#fef3c7',
    borderCell: '#fde68a',
    textColor: '#92400e',
    fontWeight: 600,
    editBadgeColor: '#d97706',
  },
  {
    key: 'activities',
    label: 'ATTIVITÀ',
    fullLabel: 'Attività',
    color: '#5f84ca',
    borderColor: '#4a70b8',
    bgCell: 'white',
    hoverBg: '#f4f8ff',
    borderCell: '#e4eaf4',
    minHeight: '300px',
    textColor: '#1a2942',
    editBadgeColor: '#5f84ca',
  },
  {
    key: 'notes',
    label: 'NOTE',
    fullLabel: 'Note',
    color: '#9fbc0c',
    borderColor: '#7d9609',
    bgCell: '#f9fce8',
    hoverBg: '#f2f8df',
    borderCell: '#d8e8a0',
    minHeight: '40px',
    textColor: '#5a6b06',
    emptyText: 'Nessuna nota',
    editBadgeColor: '#7d9609',
  },
  {
    key: 'ferie',
    label: 'FERIE',
    fullLabel: 'Ferie',
    color: '#8b5cf6',
    borderColor: '#7c3aed',
    bgCell: '#f5f3ff',
    hoverBg: '#ede9fe',
    borderCell: '#ddd6fe',
    minHeight: '40px',
    textColor: '#5b21b6',
    fontWeight: 600,
    editBadgeColor: '#7c3aed',
  },
];

const getMonday = (date: Date): Date => {
  const d = new Date(date);
  const day = d.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  d.setDate(d.getDate() + diff);
  d.setHours(0, 0, 0, 0);
  return d;
};

const addDays = (date: Date, days: number): Date => {
  const d = new Date(date);
  d.setDate(d.getDate() + days);
  return d;
};

const toKey = (date: Date): string =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;

const getISOWeek = (date: Date): number => {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const dayNum = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  return Math.ceil((((d.getTime() - yearStart.getTime()) / 86400000) + 1) / 7);
};

const formatWeekLabel = (start: Date): string => {
  const end = addDays(start, 6);
  const endMonth = MONTHS_IT[end.getMonth()];
  if (start.getMonth() === end.getMonth()) {
    return `${start.getDate()} – ${end.getDate()} ${endMonth} ${end.getFullYear()}`;
  }
  return `${start.getDate()} ${MONTHS_IT[start.getMonth()]} – ${end.getDate()} ${endMonth} ${end.getFullYear()}`;
};

const isSameDay = (a: Date, b: Date): boolean =>
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate();

interface EmployeeDayTasks {
  employeeName: string;
  vehicles: { id: number; name: string; plate: string | null }[];
  tasks: Task[];
}

// Shared by ActivityDayCell (rendering) and the "copia giornata" WhatsApp export below, so the
// employee/vehicle grouping logic (and the vehicle dedup) only lives in one place.
const groupTasksByEmployee = (tasks: Task[]): EmployeeDayTasks[] => {
  const sorted = [...tasks].sort((a, b) => new Date(a.scheduledDateTime).getTime() - new Date(b.scheduledDateTime).getTime());
  const byEmployee: Record<string, Task[]> = {};
  for (const task of sorted) {
    const name = task.employeesLabel || 'Non assegnato';
    if (!byEmployee[name]) byEmployee[name] = [];
    byEmployee[name].push(task);
  }
  return Object.entries(byEmployee).map(([employeeName, empTasks]) => ({
    employeeName,
    vehicles: Array.from(new Map(empTasks.flatMap(t => t.vehicles || []).map(v => [v.id, v])).values()),
    tasks: empTasks,
  }));
};

// Builds the WhatsApp-ready text for one day's column: reperibili, attività (raggruppate per
// dipendente, come nella cella), note e ferie. Sezioni vuote omesse, non mostrate come "nessuno".
const buildWhatsAppDaySummary = (
  dayLabel: string,
  dayNumber: number,
  monthLabel: string,
  dayTasks: Task[],
  onCall: WeeklyScheduleService.OnCall[],
  note: WeeklyScheduleService.WeeklyNote | undefined,
  ferie: WeeklyScheduleService.Ferie[]
): string => {
  const sections: string[] = [`📅 *${dayLabel.toUpperCase()} ${dayNumber} ${monthLabel.toUpperCase()}*`];

  if (onCall.length > 0) {
    const names = onCall.map(oc => `*${oc.employee.firstName} ${oc.employee.lastName}*`).join(', ');
    sections.push(`🟠 *Reperibili*\n${names}`);
  }

  if (dayTasks.length > 0) {
    const employeeBlocks = groupTasksByEmployee(dayTasks).map(({ employeeName, vehicles, tasks }) => {
      const vehicleSuffix = vehicles.length > 0 ? ` (con ${vehicles.map(v => v.name).join(', ')})` : '';
      const taskLines = tasks
        .map(t => {
          const descriptionSuffix = t.description?.trim() ? ` - ${t.description.trim()}` : '';
          return `- ORE ${formatTime(t.scheduledDateTime)} - CLIENTE ${t.customer.fullName} - ${t.title}${descriptionSuffix}`;
        })
        .join('\n');
      return `👤 *${employeeName.toUpperCase()}*${vehicleSuffix}\n${taskLines}`;
    });
    sections.push(`📋 *Attività*\n\n${employeeBlocks.join('\n\n')}`);
  }

  if (note?.notes?.trim()) {
    sections.push(`📝 *Note*\n${note.notes.trim()}`);
  }

  if (ferie.length > 0) {
    const names = ferie.map(f => `${f.employee.firstName} ${f.employee.lastName}`).join(', ');
    sections.push(`🏖️ *Ferie*\n${names}`);
  }

  return sections.join('\n\n');
};

// ═══════════════════════════════════════════════════════════════════════════════
// TASKS TABLE — helpers
// ═══════════════════════════════════════════════════════════════════════════════


const formatDateDDmmYYYY = (dateStr: string): string => {
  const d = new Date(dateStr);
  return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
};

const formatTime = (dateStr: string): string => {
  const d = new Date(dateStr);
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
};

const toDateTimeLocal = (dateStr: string): string => {
  const d = new Date(dateStr);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}T${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
};

const getEndOfCurrentYear = (): string => `${new Date().getFullYear()}-12-31`;

const buildCustomerAddressString = (customer: Customer): string => {
  const cityLine = [customer.addressZip, customer.addressCity].filter(Boolean).join(' ');
  const parts = [customer.address, customer.addressState ? `${cityLine} (${customer.addressState})` : cityLine];
  return parts.filter(Boolean).join(', ').trim();
};

interface NewCustomerFormState {
  fullName: string;
  vatNumber: string;
  taxCode: string;
  email: string;
  phone: string;
  address: string;
  addressCity: string;
  addressZip: string;
  addressState: string;
}

const EMPTY_NEW_CUSTOMER_FORM: NewCustomerFormState = {
  fullName: '', vatNumber: '', taxCode: '', email: '', phone: '',
  address: '', addressCity: '', addressZip: '', addressState: '',
};

const EMPTY_TASK_FORM: CreateTaskRequest = {
  title: '', taskType: '' as any, customerId: 0, employeeIds: [], vehicleIds: [], scheduledDateTime: '', isRecurring: false,
};

const fetchComuneInfo = async (city: string): Promise<{ provincia: string; cap: string } | null> => {
  if (!city.trim()) return null;
  try {
    const response = await fetch(`https://comuni-ita.nicolorebaioli.dev/comuni?q=${encodeURIComponent(city.trim())}`);
    if (!response.ok) return null;
    const data = await response.json();
    if (Array.isArray(data) && data.length > 0) {
      const match = data[0];
      return { provincia: match.provincia?.sigla || '', cap: match.cap || '' };
    }
    return null;
  } catch (error) {
    return null;
  }
};

// ═══════════════════════════════════════════════════════════════════════════════
// WEEKLY GRID — Sub-components
// ═══════════════════════════════════════════════════════════════════════════════

interface ActivityDayCellProps {
  tasks: Task[];
  isLast: boolean;
  borderCell: string;
  onAddTask: () => void;
  onEditTask: (task: Task) => void;
  fontScale: number;
  heightPx?: number;
  widthPx?: number;
}

function ActivityDayCell({ tasks, isLast, borderCell, onAddTask, onEditTask, fontScale, heightPx, widthPx }: ActivityDayCellProps) {
  const fs = (px: number) => `${Math.round(px * fontScale * 10) / 10}px`;
  const is = (px: number) => Math.round(px * fontScale * 10) / 10;

  const employeeGroups = groupTasksByEmployee(tasks);

  return (
    <div
      onClick={onAddTask}
      className={`weekly-grid-activity-cell${heightPx ? ' weekly-grid-activity-cell--custom-height' : ''}`}
      style={{
        ...(widthPx ? { flex: `0 0 ${widthPx}px`, width: `${widthPx}px` } : { flex: 1, minWidth: '128px' }),
        background: '#eef7e3',
        borderRight: isLast ? 'none' : `1px solid ${borderCell}`,
        borderBottom: `1px solid ${borderCell}`,
        padding: '7px 8px',
        boxSizing: 'border-box',
        cursor: 'pointer',
        ...(heightPx ? { height: `${heightPx}px`, overflowY: 'auto' as const } : { minHeight: '300px' }),
      }}
    >
      {tasks.length === 0 ? (
        <em style={{ color: '#c8d6e5', fontSize: fs(11) }}>Nessuna attività assegnata</em>
      ) : (
        employeeGroups.map(({ employeeName: empName, tasks: empTasks }) => {
          return (
            <div key={empName} style={{ marginBottom: '7px' }}>
              <div style={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap', alignItems: 'center', gap: '5px', marginBottom: '3px' }}>
                <span
                  title={empName}
                  style={{
                    background: '#688CC8',
                    color: 'white',
                    padding: '1px 6px',
                    borderRadius: '4px',
                    fontSize: fs(11),
                    lineHeight: 1.3,
                    fontWeight: 700,
                    display: 'inline-block',
                    maxWidth: '100%',
                    whiteSpace: 'normal',
                    overflowWrap: 'break-word',
                  }}
                >
                  <MdPerson size={is(10)} style={{ display: 'inline', verticalAlign: '-1px', marginRight: '3px' }} />
                  {empName}
                </span>
              </div>
              {(() => {
                const byCustomer: Record<string, Task[]> = {};
                empTasks.forEach(t => {
                  const custName = t.customer.fullName;
                  if (!byCustomer[custName]) byCustomer[custName] = [];
                  byCustomer[custName].push(t);
                });

                return Object.entries(byCustomer).map(([custName, custTasks]) => {
                  return (
                  <div key={custName} style={{ marginBottom: '6px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '2px', fontSize: fs(11), fontWeight: 700, color: '#9fbc0c', lineHeight: 1.3, marginBottom: '1px' }}>
                      <MdApartment size={is(10)} />
                      {custName}
                    </div>
                    {custTasks.map(task => (
                      <div
                        key={task.id}
                        onClick={e => {
                          e.stopPropagation();
                          onEditTask(task);
                        }}
                        onMouseEnter={e => { e.currentTarget.style.background = '#f1f5f9'; }}
                        onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
                        style={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          gap: '3px',
                          marginBottom: '4px',
                          marginLeft: '4px',
                          padding: '2px',
                          cursor: 'pointer',
                          borderRadius: '4px',
                        }}
                      >
                        <div style={{ fontSize: fs(12), lineHeight: 1.3 }}>
                          <span style={{ fontWeight: 700, color: '#000000' }}>
                            {formatTime(task.scheduledDateTime)} - {task.title}
                          </span>
                          {task.vehicles && task.vehicles.length > 0 && (
                            <span style={{ fontWeight: 600, color: '#000000' }}>
                              {' '}con {task.vehicles.map(v => v.name).join(', ')}
                            </span>
                          )}
                          {task.description && (
                            <span style={{ fontWeight: 400, color: '#000000' }}>
                              {' '}- {task.description}
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                  );
                });
              })()}
            </div>
          );
        })
      )}
    </div>
  );
}

const NAV_BTN: CSSProperties = {
  padding: '7px 14px',
  border: '1.5px solid #dde5f0',
  borderRadius: '7px',
  color: '#475569',
  fontSize: '12.5px',
  background: 'white',
  cursor: 'pointer',
  fontFamily: "'Inter', system-ui, -apple-system, sans-serif",
  lineHeight: 1,
};

const NAV_BTN_SM: CSSProperties = {
  padding: '6px 8px',
  fontSize: '11px',
};

interface TaskActionsMenuProps {
  onEdit: () => void;
  onCopy: () => void;
  onDelete: () => void;
}

// The Tooltip on the trigger button used to get stuck open after closing one
// of the Modifica/Copia/Elimina modals: Chakra's Menu returns focus to the
// MenuButton when it closes (for accessibility), and Chakra's Tooltip shows
// on focus as well as hover — so the re-focus re-triggered the tooltip with
// no mouse over it to ever fire a mouseleave and hide it again. Controlling
// isOpen manually via mouse events only (never focus) sidesteps that.
function TaskActionsMenu({ onEdit, onCopy, onDelete }: TaskActionsMenuProps) {
  const [isTooltipOpen, setIsTooltipOpen] = useState(false);

  return (
    <Menu>
      <Tooltip label="Azioni" fontSize="md" isOpen={isTooltipOpen}>
        <MenuButton
          as={Button}
          onMouseEnter={() => setIsTooltipOpen(true)}
          onMouseLeave={() => setIsTooltipOpen(false)}
          onClick={() => setIsTooltipOpen(false)}
          _hover={{ bg: 'gray.200' }} _active={{ bg: 'gray.100' }} _focus={{ boxShadow: 'none' }}
          style={{ height: '30px', width: '30px', padding: '0px', borderRadius: '6px', border: 'none', cursor: 'pointer' }}
        >
          <Icon as={MdMoreVert} w={5} h={5} color="black" />
        </MenuButton>
      </Tooltip>
      <MenuList>
        <MenuItem icon={<Icon as={MdOutlineEdit} w={4} h={4} />} onClick={() => { setIsTooltipOpen(false); onEdit(); }}>
          Modifica
        </MenuItem>
        <MenuItem icon={<Icon as={MdContentCopy} w={4} h={4} />} onClick={() => { setIsTooltipOpen(false); onCopy(); }}>
          Copia
        </MenuItem>
        <MenuItem
          icon={<Icon as={MdOutlineDeleteOutline} w={4} h={4} />}
          color="red.500"
          onClick={() => { setIsTooltipOpen(false); onDelete(); }}
        >
          Elimina
        </MenuItem>
      </MenuList>
    </Menu>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════════════════════

export default function TasksPage() {
  const { keycloak } = useKeycloak();
  const token = keycloak?.token;
  const isAdmin = hasRole(keycloak, 'admin') || hasRole(keycloak, 'ADMIN');
  const history = useHistory();

  const fullscreenContainerRef = useRef<HTMLDivElement>(null);
  const isMobileControls = useBreakpointValue({ base: true, md: false });

  // ── Weekly grid state ──────────────────────────────────────────────────────
  const [currentWeekStart, setCurrentWeekStart] = useState(() => getMonday(new Date()));
  const [gridModalState, setGridModalState] = useState<ModalState | null>(null);
  const [gridModalValue, setGridModalValue] = useState('');

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const weekDays = Array.from({ length: 7 }, (_, i) => addDays(currentWeekStart, i));

  const closeGridModal = () => setGridModalState(null);

  const getGridModalTitle = (): string => {
    if (!gridModalState) return '';
    const rowLabel = ROWS.find(r => r.key === gridModalState.row)?.label ?? '';
    const day = weekDays[gridModalState.dayIndex];
    const dayNum = day.getDate();
    return `${rowLabel} — ${DAYS_IT[gridModalState.dayIndex].toUpperCase()} ${dayNum}`;
  };

  // ── Tasks table state ──────────────────────────────────────────────────────
  const [tasks, setTasks] = useState<Task[]>([]);
  const [stats, setStats] = useState<TaskStatistics | null>(null);
  const [taskLoading, setTaskLoading] = useState(false);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [operationTypes, setOperationTypes] = useState<OperationsTipology[]>([]);
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [newTipologyName, setNewTipologyName] = useState('');
  const [addTipologyTarget, setAddTipologyTarget] = useState<'create' | 'edit' | null>(null);
  const [isSavingTipology, setIsSavingTipology] = useState(false);
  const [newCustomerForm, setNewCustomerForm] = useState<NewCustomerFormState>(EMPTY_NEW_CUSTOMER_FORM);
  const [addCustomerTarget, setAddCustomerTarget] = useState<'create' | 'edit' | null>(null);
  const [isSavingCustomer, setIsSavingCustomer] = useState(false);
  const [isLookingUpCustomerCity, setIsLookingUpCustomerCity] = useState(false);
  const [taskFilters, setTaskFilters] = useState({
    employeeId: undefined as string | undefined,
    customerId: undefined as number | undefined,
    dateFrom: '' as string,
    dateTo: '' as string,
    createdFrom: '' as string,
    createdTo: '' as string,
    reportNumber: '' as string,
    reportStatus: '' as string,
  });
  const [typedReportNumberFilter, setTypedReportNumberFilter] = useState('');
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 10 });
  const [totalPages, setTotalPages] = useState(0);
  const [inputTaskPageNumber, setInputTaskPageNumber] = useState('');
  const [selectedTaskIds, setSelectedTaskIds] = useState<number[]>([]);
  const [isBulkDeleting, setIsBulkDeleting] = useState(false);

  // ── Weekly Schedule state ──────────────────────────────────────────────────
  const [weeklyNotes, setWeeklyNotes] = useState<Record<number, WeeklyScheduleService.WeeklyNote>>({});
  const [weeklyOnCall, setWeeklyOnCall] = useState<Record<number, WeeklyScheduleService.OnCall[]>>({});
  const [onCallAddState, setOnCallAddState] = useState<number | null>(null);
  const [onCallSelectedIds, setOnCallSelectedIds] = useState<number[]>([]);
  const [weeklyFerie, setWeeklyFerie] = useState<Record<number, WeeklyScheduleService.Ferie[]>>({});
  const [ferieAddState, setFerieAddState] = useState<number | null>(null);
  const [ferieSelectedIds, setFerieSelectedIds] = useState<number[]>([]);
  const [weeklyScheduleLoading, setWeeklyScheduleLoading] = useState(false);
  const [weekTasks, setWeekTasks] = useState<Task[]>([]);
  const [activityFontScale, setActivityFontScale] = useState(1);

  const decreaseActivityFontScale = () => setActivityFontScale(s => Math.max(0.7, Math.round((s - 0.1) * 10) / 10));
  const increaseActivityFontScale = () => setActivityFontScale(s => Math.min(1.4, Math.round((s + 0.1) * 10) / 10));

  // Shared with Reperibili/Note/Ferie so the zoom control scales all four rows together
  // (ActivityDayCell has its own identical copy, scoped to the fontScale prop it receives).
  const gridFontSize = (px: number) => `${Math.round(px * activityFontScale * 10) / 10}px`;
  const gridIconSize = (px: number) => Math.round(px * activityFontScale * 10) / 10;

  // Any day can be collapsed to a thin strip via a button on its header — on entering fullscreen
  // (set in handleFullscreen) only Sunday starts collapsed by default, to free up room for the
  // other six days; the rest open as normal until the user collapses them too.
  const [collapsedColumns, setCollapsedColumns] = useState<Record<number, boolean>>({});
  const toggleColumnCollapsed = (dayIndex: number, collapsed: boolean) =>
    setCollapsedColumns(prev => ({ ...prev, [dayIndex]: collapsed }));

  // Per-row height (Reperibili/Attività/Note/Ferie), drag-resized from a handle that appears when
  // hovering the bottom edge of the row. Missing key = today's default (auto-growing) height.
  const [rowHeights, setRowHeights] = useState<Record<string, number>>({});
  const [resizingRow, setResizingRow] = useState<string | null>(null);
  const rowRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const ROW_MIN_HEIGHT = 40;
  const ROW_MAX_HEIGHT = 700;

  const startRowResize = (key: string, clientY: number) => {
    const el = rowRefs.current[key];
    const startHeight = rowHeights[key] ?? (el ? el.getBoundingClientRect().height : 120);
    setResizingRow(key);

    const handleMove = (e: MouseEvent) => {
      const next = Math.min(ROW_MAX_HEIGHT, Math.max(ROW_MIN_HEIGHT, Math.round(startHeight + (e.clientY - clientY))));
      setRowHeights(prev => ({ ...prev, [key]: next }));
    };
    const handleUp = () => {
      setResizingRow(null);
      window.removeEventListener('mousemove', handleMove);
      window.removeEventListener('mouseup', handleUp);
    };
    window.addEventListener('mousemove', handleMove);
    window.addEventListener('mouseup', handleUp);
  };

  // Per-column width (Lun–Dom), drag-resized from a handle on the right edge of each day header —
  // applied to every row so all five (header + 4 data rows) stay aligned. Missing key = today's
  // default (flex:1, shared equally).
  const [columnWidths, setColumnWidths] = useState<Record<number, number>>({});
  const [resizingColumn, setResizingColumn] = useState<number | null>(null);
  const columnRefs = useRef<Record<number, HTMLDivElement | null>>({});
  const COLUMN_MIN_WIDTH = 90;
  const COLUMN_MAX_WIDTH = 500;

  const startColumnResize = (dayIndex: number, clientX: number) => {
    const el = columnRefs.current[dayIndex];
    const startWidth = columnWidths[dayIndex] ?? (el ? el.getBoundingClientRect().width : 128);
    setResizingColumn(dayIndex);

    const handleMove = (e: MouseEvent) => {
      const next = Math.min(COLUMN_MAX_WIDTH, Math.max(COLUMN_MIN_WIDTH, Math.round(startWidth + (e.clientX - clientX))));
      setColumnWidths(prev => ({ ...prev, [dayIndex]: next }));
    };
    const handleUp = () => {
      setResizingColumn(null);
      window.removeEventListener('mousemove', handleMove);
      window.removeEventListener('mouseup', handleUp);
    };
    window.addEventListener('mousemove', handleMove);
    window.addEventListener('mouseup', handleUp);
  };

  const [selectedTask, setSelectedTask] = useState<Task | null>(null);
  const [deleteMode, setDeleteMode] = useState<DeleteMode>('ONLY_THIS');
  const [pendingStatusTask, setPendingStatusTask] = useState<Task | null>(null);
  const [form, setForm] = useState<CreateTaskRequest>(EMPTY_TASK_FORM);

  const [editTask, setEditTask] = useState<Task | null>(null);
  const [editForm, setEditForm] = useState<CreateTaskRequest>(EMPTY_TASK_FORM);
  const [addressTouched, setAddressTouched] = useState(false);
  const [editAddressTouched, setEditAddressTouched] = useState(false);
  // Id -> fullName lookup, filled in whenever a customer becomes known (search result picked, or a
  // task being edited/copied already names its customer) — the customers list itself can't be
  // preloaded client-side (backend paginates at a fixed page size), so this is what lets the
  // "Nuovo/Modifica Intervento" customer field show the selected name without a stale-reset dance
  // across every place the form gets reset.
  const [customerNameCache, setCustomerNameCache] = useState<Record<number, string>>({});

  const { isOpen: isCreateOpen, onOpen: onCreateOpen, onClose: onCreateClose } = useDisclosure();
  const { isOpen: isEditOpen, onOpen: onEditOpen, onClose: onEditClose } = useDisclosure();
  const { isOpen: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure();
  const { isOpen: isStatusOpen, onOpen: onStatusOpen, onClose: onStatusClose } = useDisclosure();
  const { isOpen: isBulkDeleteOpen, onOpen: onBulkDeleteOpen, onClose: onBulkDeleteClose } = useDisclosure();
  const { isOpen: isAddTipologyOpen, onOpen: onAddTipologyOpen, onClose: onAddTipologyClose } = useDisclosure();
  const { isOpen: isAddCustomerOpen, onOpen: onAddCustomerOpen, onClose: onAddCustomerClose } = useDisclosure();
  const deleteAlertCancelRef = useRef(null);
  const statusAlertCancelRef = useRef(null);
  const bulkDeleteAlertCancelRef = useRef(null);

  const shadowBox = useColorModeValue('14px 17px 40px 4px rgba(112, 144, 176, 0.08)', 'none');
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.100');

  const handlePreviousTaskPage = () => {
    if (pagination.pageIndex > 0) {
      setPagination(p => ({ ...p, pageIndex: p.pageIndex - 1 }));
    }
  };

  const handleNextTaskPage = () => {
    if (pagination.pageIndex < (totalPages || 1) - 1) {
      setPagination(p => ({ ...p, pageIndex: p.pageIndex + 1 }));
    }
  };

  const handleTaskFilterChange = (partial: Partial<typeof taskFilters>) => {
    setTaskFilters(prev => ({ ...prev, ...partial }));
    setPagination(prev => ({ ...prev, pageIndex: 0 }));
  };

  // 500ms debounce so typing a report number doesn't re-query on every keystroke — same pattern used
  // for the free-text filters on the Employees list.
  useEffect(() => {
    const handler = setTimeout(() => {
      handleTaskFilterChange({ reportNumber: typedReportNumberFilter });
    }, 500);
    return () => clearTimeout(handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [typedReportNumberFilter]);

  const fetchTasks = async () => {
    if (!token) return;
    setTaskLoading(true);
    try {
      const data = await TaskService.getAllPaginatedTasks(
        token,
        () => keycloak?.logout(),
        {
          pageNumber: pagination.pageIndex,
          pageSize: pagination.pageSize,
          employeeId: taskFilters.employeeId,
          customerId: taskFilters.customerId,
          dateFrom: taskFilters.dateFrom || undefined,
          dateTo: taskFilters.dateTo || undefined,
          createdFrom: taskFilters.createdFrom || undefined,
          createdTo: taskFilters.createdTo || undefined,
          reportNumber: taskFilters.reportNumber || undefined,
          reportStatus: taskFilters.reportStatus || undefined,
        }
      );
      if (data) {
        setTasks(data.content);
        setTotalPages(data.totalPages);
        setSelectedTaskIds([]);
      }
    } catch (err) {
      console.error('Error fetching tasks:', err);
    } finally {
      setTaskLoading(false);
    }
  };

  const fetchStats = async () => {
    if (!token) return;
    try {
      const data = await TaskService.getTaskStatistics(
        token,
        () => keycloak?.logout(),
        {
          employeeId: taskFilters.employeeId,
        }
      );
      if (data) setStats(data);
    } catch (err) {
      console.error('Error fetching stats:', err);
    }
  };

  const fetchEmployees = async () => {
    if (!token) return;
    try {
      const data = await EmployeeService.getAllPaginated(token, () => keycloak?.logout(), 0, 100);
      if (data) setEmployees(data.content);
    } catch (err) {
      console.error('Error fetching employees:', err);
    }
  };

  const fetchOperationTypes = async () => {
    if (!token) return;
    try {
      const data = await OperationsTipologyService.getAll(token, () => keycloak?.logout());
      setOperationTypes(data);
    } catch (err) {
      console.error('Error fetching operation types:', err);
    }
  };

  const fetchVehicles = async () => {
    if (!token) return;
    try {
      const data = await VehicleService.getAll(token, () => keycloak?.logout());
      setVehicles(data);
    } catch (err) {
      console.error('Error fetching vehicles:', err);
    }
  };

  const fetchWeeklyNotes = async (weekStart: Date) => {
    if (!token) return;
    try {
      const weekStartStr = toKey(weekStart);
      const data = await WeeklyScheduleService.getNotesByWeek(token, () => keycloak?.logout(), weekStartStr);
      if (data) {
        const notesMap: Record<number, WeeklyScheduleService.WeeklyNote> = {};
        data.forEach(note => {
          notesMap[note.dayOfWeek] = note;
        });
        setWeeklyNotes(notesMap);
      }
    } catch (err) {
      console.error('Error fetching weekly notes:', err);
    }
  };

  const fetchWeeklyOnCall = async (weekStart: Date) => {
    if (!token) return;
    try {
      const weekStartStr = toKey(weekStart);
      const data = await WeeklyScheduleService.getOnCallByWeek(token, () => keycloak?.logout(), weekStartStr);
      if (data) {
        const onCallMap: Record<number, WeeklyScheduleService.OnCall[]> = {};
        for (let i = 1; i <= 7; i++) {
          onCallMap[i] = [];
        }
        data.forEach(onCall => {
          if (!onCallMap[onCall.dayOfWeek]) onCallMap[onCall.dayOfWeek] = [];
          onCallMap[onCall.dayOfWeek].push(onCall);
        });
        setWeeklyOnCall(onCallMap);
      }
    } catch (err) {
      console.error('Error fetching weekly on-call:', err);
    }
  };

  const fetchWeeklyFerie = async (weekStart: Date) => {
    if (!token) return;
    try {
      const weekStartStr = toKey(weekStart);
      const data = await WeeklyScheduleService.getFerieByWeek(token, () => keycloak?.logout(), weekStartStr);
      if (data) {
        const ferieMap: Record<number, WeeklyScheduleService.Ferie[]> = {};
        for (let i = 1; i <= 7; i++) {
          ferieMap[i] = [];
        }
        data.forEach(ferie => {
          if (!ferieMap[ferie.dayOfWeek]) ferieMap[ferie.dayOfWeek] = [];
          ferieMap[ferie.dayOfWeek].push(ferie);
        });
        setWeeklyFerie(ferieMap);
      }
    } catch (err) {
      console.error('Error fetching weekly ferie:', err);
    }
  };

  // Copies one day's full schedule (reperibili/attività/note/ferie) as WhatsApp-ready text — the
  // client shares the daily plan with the crew over WhatsApp and was doing it by hand.
  const handleCopyDaySummary = async (day: Date, dayIndex: number) => {
    const dayOfWeek = dayIndex + 1;
    const dayTasks = weekTasks.filter(t => isSameDay(new Date(t.scheduledDateTime), day));
    const summary = buildWhatsAppDaySummary(
      DAYS_IT[dayIndex],
      day.getDate(),
      MONTHS_IT[day.getMonth()],
      dayTasks,
      weeklyOnCall[dayOfWeek] || [],
      weeklyNotes[dayOfWeek],
      weeklyFerie[dayOfWeek] || []
    );
    try {
      await navigator.clipboard.writeText(summary);
      toast.success(`Programma di ${DAYS_IT[dayIndex].toLowerCase()} ${day.getDate()} copiato!`);
    } catch (err) {
      console.error('Error copying day summary:', err);
      toast.error('Impossibile copiare il programma negli appunti');
    }
  };

  const fetchWeekTasks = async (weekStart: Date) => {
    if (!token) return;
    try {
      const data = await TaskService.getAllPaginatedTasks(
        token,
        () => keycloak?.logout(),
        {
          pageNumber: 0,
          pageSize: 500,
          employeeId: taskFilters.employeeId,
          dateFrom: toKey(weekStart),
          dateTo: toKey(addDays(weekStart, 6)),
        }
      );
      if (data) {
        setWeekTasks(data.content);
      }
    } catch (err) {
      console.error('Error fetching week tasks:', err);
    }
  };

  useEffect(() => {
    fetchEmployees();
    fetchOperationTypes();
    fetchVehicles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => {
    fetchTasks();
    fetchStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taskFilters, pagination, token]);

  useEffect(() => {
    fetchWeeklyNotes(currentWeekStart);
    fetchWeeklyOnCall(currentWeekStart);
    fetchWeeklyFerie(currentWeekStart);
    fetchWeekTasks(currentWeekStart);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentWeekStart, token, taskFilters.employeeId]);

  useEffect(() => {
    if (isCreateOpen) setAddressTouched(false);
  }, [isCreateOpen]);

  useEffect(() => {
    if (isEditOpen) setEditAddressTouched(false);
  }, [isEditOpen]);

  const getTaskFormDisabledReason = (f: CreateTaskRequest): string => {
    if (!f.title.trim()) return 'Inserisci il titolo dell\'intervento';
    if (!f.taskType) return 'Seleziona il tipo di intervento';
    if (!f.customerId) return 'Seleziona il cliente';
    if (f.employeeIds.length === 0) return 'Seleziona almeno un dipendente';
    if (!validateAddress(f.address || '')) return 'Inserisci un indirizzo valido per salvare l\'intervento';
    if (!f.scheduledDateTime) return 'Inserisci la data e ora pianificata';
    if (f.isRecurring && !f.recurrenceType) return 'Seleziona il tipo di ricorrenza';
    return '';
  };

  const handleOpenCreateForDay = (day: Date) => {
    const y = day.getFullYear();
    const m = String(day.getMonth() + 1).padStart(2, '0');
    const d = String(day.getDate()).padStart(2, '0');
    setForm({ ...EMPTY_TASK_FORM, scheduledDateTime: `${y}-${m}-${d}T09:00` });
    setAddressTouched(false);
    onCreateOpen();
  };

  const handleCreateClose = () => {
    onCreateClose();
    setForm(EMPTY_TASK_FORM);
    setAddressTouched(false);
  };

  const handleEditClose = () => {
    onEditClose();
    setEditTask(null);
    setEditForm(EMPTY_TASK_FORM);
    setEditAddressTouched(false);
  };

  const openAddTipologyModal = (target: 'create' | 'edit') => {
    setAddTipologyTarget(target);
    setNewTipologyName('');
    onAddTipologyOpen();
  };

  const handleSaveNewTipology = async () => {
    if (!token || !newTipologyName.trim()) return;
    setIsSavingTipology(true);
    const result = await OperationsTipologyService.create(token, () => keycloak?.logout(), { name: newTipologyName.trim() });
    if (result) {
      toast.success('Tipologia intervento creata con successo');
      setOperationTypes(prev => [...prev, result]);
      if (addTipologyTarget === 'create') {
        setForm(f => ({ ...f, taskType: result.name as any }));
      } else if (addTipologyTarget === 'edit') {
        setEditForm(f => ({ ...f, taskType: result.name as any }));
      }
      onAddTipologyClose();
    } else {
      toast.error('Errore durante la creazione della tipologia intervento');
    }
    setIsSavingTipology(false);
  };

  const openAddCustomerModal = (target: 'create' | 'edit') => {
    setAddCustomerTarget(target);
    setNewCustomerForm(EMPTY_NEW_CUSTOMER_FORM);
    onAddCustomerOpen();
  };

  const handleNewCustomerCityBlur = async () => {
    if (!newCustomerForm.addressCity.trim()) return;
    setIsLookingUpCustomerCity(true);
    const info = await fetchComuneInfo(newCustomerForm.addressCity);
    if (info) {
      setNewCustomerForm(f => ({
        ...f,
        addressState: info.provincia || f.addressState,
        addressZip: info.cap || f.addressZip,
      }));
    }
    setIsLookingUpCustomerCity(false);
  };

  const handleSaveNewCustomer = async () => {
    if (!token || !newCustomerForm.fullName.trim()) return;
    if (newCustomerForm.vatNumber.trim() && !validateItalianVAT(newCustomerForm.vatNumber.trim())) {
      toast.error('Partita IVA non valida. Deve essere composta da 11 cifre.');
      return;
    }
    if (newCustomerForm.taxCode.trim() && !validateItalianTaxCode(newCustomerForm.taxCode.trim())) {
      toast.error('Codice Fiscale non valido.');
      return;
    }
    if (newCustomerForm.email.trim() && !validateEmail(newCustomerForm.email.trim())) {
      toast.error('Formato email non corretto.');
      return;
    }

    setIsSavingCustomer(true);
    const result = await CustomersService.create(token, () => keycloak?.logout(), {
      fullName: newCustomerForm.fullName.trim(),
      vatNumber: newCustomerForm.vatNumber.trim() || undefined,
      taxCode: newCustomerForm.taxCode.trim() || undefined,
      email: newCustomerForm.email.trim() || undefined,
      phone: newCustomerForm.phone.trim() || undefined,
      address: newCustomerForm.address.trim() || undefined,
      addressCity: newCustomerForm.addressCity.trim() || undefined,
      addressZip: newCustomerForm.addressZip.trim() || undefined,
      addressState: newCustomerForm.addressState.trim() || undefined,
    });

    if (result) {
      toast.success('Cliente creato con successo');
      setCustomerNameCache(prev => ({ ...prev, [result.id]: result.fullName }));
      const customerAddress = buildCustomerAddressString(result);
      if (addCustomerTarget === 'create') {
        setForm(f => ({ ...f, customerId: result.id, address: customerAddress || f.address }));
        setAddressTouched(false);
      } else if (addCustomerTarget === 'edit') {
        setEditForm(f => ({ ...f, customerId: result.id, address: customerAddress || f.address }));
      }
      onAddCustomerClose();
    } else {
      toast.error('Errore durante la creazione del cliente');
    }
    setIsSavingCustomer(false);
  };

  const handleCreateTask = async () => {
    if (!token || !form.title || form.employeeIds.length === 0 || !form.customerId || !form.scheduledDateTime || !validateAddress(form.address || '')) {
      toast.error('Compila tutti i campi obbligatori');
      return;
    }
    if (form.isRecurring && !form.recurrenceType) {
      toast.error('Seleziona il tipo di ricorrenza');
      return;
    }
    try {
      const payload: CreateTaskRequest = {
        ...form,
        recurrenceEndDate: form.isRecurring ? (form.recurrenceEndDate || getEndOfCurrentYear()) : undefined,
      };
      const res = await TaskService.createTask(token, () => keycloak?.logout(), payload);
      if (res) {
        toast.success('Intervento creato con successo');
        onCreateClose();
        setForm(EMPTY_TASK_FORM);
        setAddressTouched(false);
        fetchTasks();
        fetchStats();
        fetchWeekTasks(currentWeekStart);
      }
    } catch (err) {
      toast.error('Errore durante la creazione del intervento');
    }
  };

  const openEditModal = (task: Task) => {
    setEditTask(task);
    setCustomerNameCache(prev => ({ ...prev, [task.customer.id]: task.customer.fullName }));
    setEditForm({
      title: task.title,
      taskType: task.taskType,
      customerId: task.customer.id,
      employeeIds: (task.employees || []).map(e => e.id),
      vehicleIds: (task.vehicles || []).map(v => v.id),
      address: task.address || '',
      scheduledDateTime: toDateTimeLocal(task.scheduledDateTime),
      description: task.description || '',
      isRecurring: task.isRecurring,
      recurrenceType: task.recurrenceType ?? undefined,
      recurrenceInterval: task.recurrenceInterval ?? undefined,
      recurrenceEndDate: task.recurrenceEndDate ?? undefined,
    });
    onEditOpen();
  };

  const handleUpdateTask = async () => {
    if (!token || !editTask) return;
    if (!editForm.title || editForm.employeeIds.length === 0 || !editForm.customerId || !editForm.scheduledDateTime || !validateAddress(editForm.address || '')) {
      toast.error('Compila tutti i campi obbligatori');
      return;
    }
    if (editForm.isRecurring && !editForm.recurrenceType) {
      toast.error('Seleziona il tipo di ricorrenza');
      return;
    }
    try {
      const res = await TaskService.updateTask(token, () => keycloak?.logout(), editTask.id, {
        title: editForm.title,
        taskType: editForm.taskType,
        customerId: editForm.customerId,
        employeeIds: editForm.employeeIds,
        vehicleIds: editForm.vehicleIds,
        address: editForm.address,
        scheduledDateTime: editForm.scheduledDateTime,
        description: editForm.description,
        isRecurring: editForm.isRecurring,
        recurrenceType: editForm.isRecurring ? editForm.recurrenceType : undefined,
        recurrenceInterval: editForm.isRecurring ? editForm.recurrenceInterval : undefined,
        recurrenceEndDate: editForm.isRecurring ? (editForm.recurrenceEndDate || getEndOfCurrentYear()) : undefined,
      });
      if (res) {
        toast.success('Intervento aggiornato con successo');
        onEditClose();
        setEditTask(null);
        setEditForm(EMPTY_TASK_FORM);
        setEditAddressTouched(false);
        fetchTasks();
        fetchStats();
        fetchWeekTasks(currentWeekStart);
      }
    } catch (err) {
      toast.error('Errore durante la modifica dell\'intervento');
    }
  };

  const handleDeleteTask = async () => {
    if (!token || !selectedTask) return;
    try {
      const success = await TaskService.deleteTask(token, () => keycloak?.logout(), selectedTask.id, deleteMode);
      if (success) {
        toast.success('Intervento eliminato con successo');
        onDeleteClose();
        setSelectedTask(null);
        fetchTasks();
        fetchStats();
        fetchWeekTasks(currentWeekStart);
      }
    } catch (err) {
      toast.error('Errore durante l\'eliminazione');
    }
  };

  const toggleSelectTask = (id: number) => {
    setSelectedTaskIds(prev => (prev.includes(id) ? prev.filter(taskId => taskId !== id) : [...prev, id]));
  };

  const toggleSelectAllTasks = () => {
    setSelectedTaskIds(prev => (prev.length === tasks.length ? [] : tasks.map(t => t.id)));
  };

  const handleBulkDeleteTasks = async () => {
    if (!token || selectedTaskIds.length === 0) return;
    setIsBulkDeleting(true);
    try {
      const success = await TaskService.deleteBulkTasks(token, () => keycloak?.logout(), selectedTaskIds, 'ONLY_THIS');

      if (success) {
        toast.success('Interventi selezionati eliminati con successo');
        setSelectedTaskIds([]);
        onBulkDeleteClose();
        fetchTasks();
        fetchStats();
        fetchWeekTasks(currentWeekStart);
      } else {
        toast.error('Errore durante l\'eliminazione degli interventi selezionati');
      }
    } finally {
      setIsBulkDeleting(false);
    }
  };

  const handleStatusClick = (task: Task) => {
    if (task.status === 'COMPLETATO') return;
    setPendingStatusTask(task);
    onStatusOpen();
  };

  const handleStatusConfirm = async () => {
    if (!token || !pendingStatusTask) return;
    const nextStatus: TaskStatus = pendingStatusTask.status === 'DA_INIZIARE' ? 'IN_CORSO' : 'COMPLETATO';
    try {
      const res = await TaskService.updateTaskStatus(
        token,
        () => keycloak?.logout(),
        pendingStatusTask.id,
        { status: nextStatus }
      );
      if (res) {
        toast.success('Stato aggiornato');
        onStatusClose();
        setPendingStatusTask(null);
        fetchTasks();
        fetchStats();
        fetchWeekTasks(currentWeekStart);
      }
    } catch (err) {
      toast.error('Errore durante l\'aggiornamento');
    }
  };

  const handleSaveNote = async () => {
    if (!token || !gridModalState || gridModalState.row !== 'notes') return;
    const weekStartStr = toKey(currentWeekStart);
    const dayOfWeek = gridModalState.dayIndex + 1;
    try {
      const res = await WeeklyScheduleService.createOrUpdateNote(
        token,
        () => keycloak?.logout(),
        weekStartStr,
        dayOfWeek,
        gridModalValue
      );
      if (res) {
        toast.success('Nota salvata');
        closeGridModal();
        setGridModalValue('');
        fetchWeeklyNotes(currentWeekStart);
      }
    } catch (err) {
      toast.error('Errore durante il salvataggio della nota');
    }
  };

  // One POST per selected employee (no bulk endpoint on the backend) — fine for the handful of
  // people picked at once here, and avoids adding a new API just for this.
  const handleAddOnCall = async (employeeIds: number[]) => {
    if (!token || !onCallAddState || employeeIds.length === 0) return;
    const weekStartStr = toKey(currentWeekStart);
    const dayOfWeek = onCallAddState;
    try {
      const results = await Promise.all(
        employeeIds.map(employeeId =>
          WeeklyScheduleService.addOnCall(token, () => keycloak?.logout(), weekStartStr, dayOfWeek, employeeId)
        )
      );
      const successCount = results.filter(Boolean).length;
      if (successCount > 0) {
        toast.success(successCount === 1 ? 'Reperibile aggiunto' : `${successCount} reperibili aggiunti`);
        fetchWeeklyOnCall(currentWeekStart);
      }
      if (successCount < employeeIds.length) {
        toast.error('Alcuni reperibili non sono stati aggiunti');
      }
    } catch (err) {
      toast.error('Errore durante l\'aggiunta dei reperibili');
    } finally {
      setOnCallAddState(null);
    }
  };

  const handleRemoveOnCall = async (dayOfWeek: number, employeeId: number) => {
    if (!token) return;
    const weekStartStr = toKey(currentWeekStart);
    try {
      const success = await WeeklyScheduleService.removeOnCall(
        token,
        () => keycloak?.logout(),
        dayOfWeek,
        employeeId,
        weekStartStr
      );
      if (success) {
        toast.success('Reperibile rimosso');
        fetchWeeklyOnCall(currentWeekStart);
      }
    } catch (err) {
      toast.error('Errore durante la rimozione del reperibile');
    }
  };

  const handleAddFerie = async (employeeIds: number[]) => {
    if (!token || !ferieAddState || employeeIds.length === 0) return;
    const weekStartStr = toKey(currentWeekStart);
    const dayOfWeek = ferieAddState;
    try {
      const results = await Promise.all(
        employeeIds.map(employeeId =>
          WeeklyScheduleService.addFerie(token, () => keycloak?.logout(), weekStartStr, dayOfWeek, employeeId)
        )
      );
      const successCount = results.filter(Boolean).length;
      if (successCount > 0) {
        toast.success(successCount === 1 ? 'Ferie aggiunte' : `Ferie aggiunte per ${successCount} dipendenti`);
        fetchWeeklyFerie(currentWeekStart);
      }
      if (successCount < employeeIds.length) {
        toast.error('Alcune ferie non sono state aggiunte');
      }
    } catch (err) {
      toast.error('Errore durante l\'aggiunta delle ferie');
    } finally {
      setFerieAddState(null);
    }
  };

  const handleRemoveFerie = async (dayOfWeek: number, employeeId: number) => {
    if (!token) return;
    const weekStartStr = toKey(currentWeekStart);
    try {
      const success = await WeeklyScheduleService.removeFerie(
        token,
        () => keycloak?.logout(),
        dayOfWeek,
        employeeId,
        weekStartStr
      );
      if (success) {
        toast.success('Ferie rimosse');
        fetchWeeklyFerie(currentWeekStart);
      }
    } catch (err) {
      toast.error('Errore durante la rimozione delle ferie');
    }
  };

  const handleRefreshWeeklySchedule = async () => {
    setWeeklyScheduleLoading(true);
    try {
      await Promise.all([
        fetchWeeklyNotes(currentWeekStart),
        fetchWeeklyOnCall(currentWeekStart),
        fetchWeeklyFerie(currentWeekStart),
        fetchWeekTasks(currentWeekStart),
      ]);
      toast.success('Piano settimanale aggiornato');
    } catch (err) {
      toast.error('Errore durante l\'aggiornamento del piano settimanale');
    } finally {
      setWeeklyScheduleLoading(false);
    }
  };

  const handleFullscreen = async () => {
    if (!fullscreenContainerRef.current) {
      toast.error('Impossibile trovare la tabella');
      return;
    }
    try {
      if (!document.fullscreenElement) {
        await fullscreenContainerRef.current.requestFullscreen();
        setCollapsedColumns({ 6: true });
      } else {
        await document.exitFullscreen();
      }
    } catch (err) {
      toast.error('Errore durante l\'apertura a schermo intero');
    }
  };

  const handleCopyTask = (taskToCopy: Task) => {
    setCustomerNameCache(prev => ({ ...prev, [taskToCopy.customer.id]: taskToCopy.customer.fullName }));
    setForm({
      title: taskToCopy.title,
      taskType: taskToCopy.taskType,
      customerId: taskToCopy.customer.id,
      employeeIds: (taskToCopy.employees || []).map(e => e.id),
      vehicleIds: (taskToCopy.vehicles || []).map(v => v.id),
      scheduledDateTime: taskToCopy.scheduledDateTime,
      isRecurring: taskToCopy.isRecurring,
      description: taskToCopy.description,
      address: taskToCopy.address,
      recurrenceType: taskToCopy.recurrenceType,
      recurrenceInterval: taskToCopy.recurrenceInterval,
      recurrenceEndDate: taskToCopy.recurrenceEndDate,
    });
    onCreateOpen();
  };

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <Box
      pt={{ base: '28px', md: '56px', xl: '54px' }}
      minH="100vh"
      style={{ fontFamily: "'Inter', system-ui, -apple-system, sans-serif", fontSize: '14px', color: '#1a2942' }}
    >
      <Box ref={fullscreenContainerRef} w="full">

        {/* ── CONTROLS BAR ───────────────────────────────────────────────────── */}
        <Box bg="white" borderBottom="1px solid #e4eaf4" px={{ base: '12px', md: '26px' }} py={{ base: '8px', md: '13px' }} className="no-print">
          {isMobileControls ? (
            <Flex direction="column" gap="6px">
              <Flex align="center" justify="center" gap="6px">
                <button style={{ ...NAV_BTN, ...NAV_BTN_SM }} onClick={() => setCurrentWeekStart(d => addDays(d, -7))}>‹</button>
                <Text flex={1} fontSize="12.5px" fontWeight={700} color="#9fbc0c" textAlign="center">
                  {formatWeekLabel(currentWeekStart)}
                </Text>
                <button style={{ ...NAV_BTN, ...NAV_BTN_SM }} onClick={() => setCurrentWeekStart(d => addDays(d, 7))}>›</button>
              </Flex>
              <Flex align="center" justify="center" gap="5px" flexWrap="wrap">
                <button style={{ ...NAV_BTN, ...NAV_BTN_SM }} onClick={() => setCurrentWeekStart(getMonday(new Date()))}>
                  Oggi
                </button>
                <Box bg="#eef2f7" borderRadius="20px" px="8px" py="3px" fontSize="10.5px" fontWeight={700} color="#475569" display="inline-block">
                  Sett. {getISOWeek(currentWeekStart)}
                </Box>
                <button
                  style={{ ...NAV_BTN, ...NAV_BTN_SM, padding: '6px 7px' }}
                  onClick={decreaseActivityFontScale}
                  disabled={activityFontScale <= 0.7}
                  title="Rimpicciolisci testo (Reperibili, Attività, Note, Ferie)"
                >
                  <MdZoomOut size={14} />
                </button>
                <button
                  style={{ ...NAV_BTN, ...NAV_BTN_SM, padding: '6px 7px' }}
                  onClick={increaseActivityFontScale}
                  disabled={activityFontScale >= 1.4}
                  title="Ingrandisci testo (Reperibili, Attività, Note, Ferie)"
                >
                  <MdZoomIn size={14} />
                </button>
                <button
                  style={{ ...NAV_BTN, ...NAV_BTN_SM, padding: '6px 7px' }}
                  onClick={handleRefreshWeeklySchedule}
                  disabled={weeklyScheduleLoading}
                  title="Aggiorna"
                >
                  {weeklyScheduleLoading ? '...' : '↻'}
                </button>
                <button style={{ ...NAV_BTN, ...NAV_BTN_SM, padding: '6px 7px' }} onClick={handleFullscreen} title="Schermo Intero">
                  ⛶
                </button>
              </Flex>
            </Flex>
          ) : (
            <Flex justify="space-between" align="center">
              <Flex align="center" gap="10px">
                <button style={NAV_BTN} onClick={() => setCurrentWeekStart(d => addDays(d, -7))}>
                  ‹ Precedente
                </button>
                <Text fontSize="14px" fontWeight={700} color="#9fbc0c" minW="256px" textAlign="center">
                  {formatWeekLabel(currentWeekStart)}
                </Text>
                <button style={NAV_BTN} onClick={() => setCurrentWeekStart(d => addDays(d, 7))}>
                  Successiva ›
                </button>
                <button style={NAV_BTN} onClick={() => setCurrentWeekStart(getMonday(new Date()))}>
                  Oggi
                </button>
                <Box bg="#eef2f7" borderRadius="20px" px="12px" py="4px" fontSize="11.5px" fontWeight={700} color="#475569" display="inline-block">
                  Sett. {getISOWeek(currentWeekStart)}
                </Box>
              </Flex>
              <Flex align="center" gap="10px">
                <button
                  style={{ ...NAV_BTN, padding: '7px 9px' }}
                  onClick={decreaseActivityFontScale}
                  disabled={activityFontScale <= 0.7}
                  title="Rimpicciolisci testo (Reperibili, Attività, Note, Ferie)"
                >
                  <MdZoomOut size={16} />
                </button>
                <button
                  style={{ ...NAV_BTN, padding: '7px 9px' }}
                  onClick={increaseActivityFontScale}
                  disabled={activityFontScale >= 1.4}
                  title="Ingrandisci testo (Reperibili, Attività, Note, Ferie)"
                >
                  <MdZoomIn size={16} />
                </button>
                <button style={NAV_BTN} onClick={handleRefreshWeeklySchedule} disabled={weeklyScheduleLoading}>
                  {weeklyScheduleLoading ? 'Aggiornamento...' : '↻ Aggiorna'}
                </button>
                <button style={NAV_BTN} onClick={handleFullscreen}>
                  ⛶ Schermo Intero
                </button>
              </Flex>
            </Flex>
          )}
        </Box>

        {/* ── WEEKLY GRID ────────────────────────────────────────────────────── */}
        <Box
          pl="10px"
          pr="20px"
          mx="0px"
          width="100%"
          py="20px"
          overflowX="auto"
          className="print-weekly-grid-container"
        >
          <Box
            bg="white"
            borderRadius="10px"
            boxShadow="0 2px 10px rgba(30,58,110,0.08)"
            overflow="hidden"
            minW="1000px"
            className="print-weekly-grid"
          >
            {/* Day header row */}
            <Flex>
              <Box w="36px" flexShrink={0} bg="#f7f9fc" borderRight="2px solid #e4eaf4" borderBottom="1px solid #e4eaf4" />
              {weekDays.map((day, i) => {
                const isToday = isSameDay(day, today);
                const isWeekend = i >= 5;

                if (collapsedColumns[i]) {
                  return (
                    <Tooltip key={i} label={`Apri la colonna di ${DAYS_IT[i]}`} fontSize="xs">
                      <Box
                        as="button"
                        onClick={() => toggleColumnCollapsed(i, false)}
                        aria-label={`Apri la colonna di ${DAYS_IT[i]}`}
                        flex="0 0 24px"
                        w="24px"
                        bg="#f8fafc"
                        borderBottom="1px solid #e4eaf4"
                        display="flex"
                        flexDirection="column"
                        alignItems="center"
                        justifyContent="center"
                        gap="2px"
                        py="6px"
                        cursor="pointer"
                      >
                        <Icon as={MdChevronLeft} boxSize="14px" color="#94a3b8" />
                        <Text fontSize="8px" fontWeight={700} letterSpacing="1.2px" color="#94a3b8" sx={{ writingMode: 'vertical-lr' }} transform="rotate(180deg)">
                          {DAYS_SHORT[i]}
                        </Text>
                      </Box>
                    </Tooltip>
                  );
                }

                const colWidth = columnWidths[i];
                return (
                  <Box
                    key={i}
                    ref={(el: HTMLDivElement | null) => { columnRefs.current[i] = el; }}
                    position="relative"
                    flex={colWidth ? `0 0 ${colWidth}px` : 1}
                    minW={colWidth ? `${colWidth}px` : '128px'}
                    w={colWidth ? `${colWidth}px` : undefined}
                    bg={isToday ? '#f0f6e8' : isWeekend ? '#f8fafc' : 'white'}
                    borderRight={i < 6 ? '1px solid #e4eaf4' : 'none'}
                    borderBottom="1px solid #e4eaf4"
                    p="6px 8px"
                  >
                    <div
                      onMouseDown={e => { e.preventDefault(); e.stopPropagation(); startColumnResize(i, e.clientX); }}
                      title="Trascina per ridimensionare la colonna"
                      className="weekly-grid-col-handle"
                      style={{
                        position: 'absolute',
                        top: 0,
                        bottom: 0,
                        // Inset within this column's own 8px right padding (never protruding into the
                        // next column's box, and narrow enough to clear the copy/collapse buttons —
                        // their Flex content sits inside that same padding, so its right edge is
                        // already 8px clear of the border). position:absolute is relative to the
                        // nearest positioned ancestor's PADDING edge, so right:0 lands right at this
                        // column's own border without ever covering the sibling column — unlike the
                        // earlier protruding version, which a later sibling box painted over.
                        right: 0,
                        width: '6px',
                        cursor: 'col-resize',
                        zIndex: 5,
                        background: resizingColumn === i ? 'rgba(95,132,202,0.35)' : 'transparent',
                      }}
                    />
                    <Flex justify="space-between" align="flex-start" gap={1}>
                      <Flex align="baseline" gap="5px">
                        <Text fontSize="9px" fontWeight={700} letterSpacing="1.2px" color="#94a3b8" textTransform="uppercase" lineHeight={1}>
                          {DAYS_SHORT[i]}
                        </Text>
                        <Text fontSize="15px" fontWeight={800} color={isToday ? '#5f84ca' : isWeekend ? '#94a3b8' : '#3d5a8a'} lineHeight={1}>
                          {day.getDate()}
                        </Text>
                        {isToday && (
                          <Box bg="#edf4d0" color="#6a7e08" fontSize="8px" borderRadius="10px" px="6px" py="1px" fontWeight={700}>
                            OGGI
                          </Box>
                        )}
                      </Flex>
                      <Flex align="center" gap="4px">
                        <Tooltip label={`Chiudi la colonna di ${DAYS_IT[i]}`} fontSize="xs">
                          <button
                            onClick={e => { e.stopPropagation(); toggleColumnCollapsed(i, true); }}
                            aria-label={`Chiudi la colonna di ${DAYS_IT[i]}`}
                            style={{
                              border: '1px solid #e4eaf4',
                              background: 'white',
                              color: '#64748b',
                              width: '22px',
                              height: '22px',
                              borderRadius: '6px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              cursor: 'pointer',
                              flexShrink: 0,
                              padding: 0,
                            }}
                          >
                            <MdChevronRight size={14} />
                          </button>
                        </Tooltip>
                        <Tooltip label={`Copia il programma di ${DAYS_IT[i]} ${day.getDate()}`} fontSize="xs">
                          <button
                            onClick={e => { e.stopPropagation(); handleCopyDaySummary(day, i); }}
                            aria-label={`Copia il programma di ${DAYS_IT[i]} ${day.getDate()}`}
                            style={{
                              border: '1px solid #e4eaf4',
                              background: 'white',
                              color: '#64748b',
                              width: '22px',
                              height: '22px',
                              borderRadius: '6px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              cursor: 'pointer',
                              flexShrink: 0,
                              padding: 0,
                            }}
                          >
                            <MdContentCopy size={12} />
                          </button>
                        </Tooltip>
                      </Flex>
                    </Flex>
                  </Box>
                );
              })}
            </Flex>

            {/* Data rows */}
            {ROWS.map(row => {
              const customHeight = rowHeights[row.key];
              return (
              <Box
                key={row.key}
                position="relative"
                ref={(el: HTMLDivElement | null) => { rowRefs.current[row.key] = el; }}
              >
              <Flex>
                <Tooltip label={row.fullLabel} placement="right">
                  <Box
                    w="36px"
                    flexShrink={0}
                    bg={row.color}
                    borderRight={`2px solid ${row.borderColor}`}
                    borderBottom="1px solid rgba(0,0,0,0.06)"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    minH={customHeight ? `${customHeight}px` : row.minHeight}
                    cursor="help"
                  >
                    <span
                      style={{
                        writingMode: 'vertical-lr',
                        fontSize: '8px',
                        fontWeight: 800,
                        color: 'white',
                        letterSpacing: '2px',
                        userSelect: 'none',
                        whiteSpace: 'nowrap',
                        transform: 'rotate(180deg)',
                      }}
                    >
                      {row.label}
                    </span>
                  </Box>
                </Tooltip>
                {weekDays.map((day, dayIndex) => {
                  const dayOfWeek = dayIndex + 1;
                  const colWidth = columnWidths[dayIndex];
                  if (collapsedColumns[dayIndex]) {
                    return (
                      <div
                        key={dayIndex}
                        style={{
                          flex: '0 0 24px',
                          width: '24px',
                          background: row.bgCell,
                          borderBottom: `1px solid ${row.borderCell}`,
                          boxSizing: 'border-box',
                        }}
                      />
                    );
                  }
                  if (row.key === 'activities') {
                    const dayTasks = weekTasks.filter(t => isSameDay(new Date(t.scheduledDateTime), day));
                    return (
                      <ActivityDayCell
                        key={dayIndex}
                        tasks={dayTasks}
                        isLast={dayIndex === 6}
                        borderCell={row.borderCell}
                        onAddTask={() => handleOpenCreateForDay(day)}
                        onEditTask={openEditModal}
                        fontScale={activityFontScale}
                        heightPx={customHeight}
                        widthPx={colWidth}
                      />
                    );
                  }
                  if (row.key === 'reperibili') {
                    const dayOnCall = weeklyOnCall[dayOfWeek] || [];
                    return (
                      <div
                        key={dayIndex}
                        onClick={() => { setOnCallSelectedIds([]); setOnCallAddState(dayOfWeek); }}
                        style={{
                          ...(colWidth ? { flex: `0 0 ${colWidth}px`, width: `${colWidth}px` } : { flex: 1, minWidth: '128px' }),
                          background: '#fffbeb',
                          borderRight: dayIndex < 6 ? '1px solid #fde68a' : 'none',
                          borderBottom: '1px solid #fde68a',
                          padding: '6px 8px',
                          cursor: 'pointer',
                          boxSizing: 'border-box',
                          display: 'flex',
                          flexDirection: 'column',
                          justifyContent: 'flex-start',
                          gap: '4px',
                          ...(customHeight ? { height: `${customHeight}px`, overflowY: 'auto' as const } : {}),
                        }}
                      >
                        {dayOnCall.length === 0 ? (
                          <em style={{ color: '#c8d6e5', fontSize: gridFontSize(10) }}>Clicca per aggiungere</em>
                        ) : (
                          <div>
                            {dayOnCall.map(onCall => (
                              <div
                                key={onCall.id}
                                style={{
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  alignItems: 'center',
                                  fontSize: gridFontSize(11),
                                  color: '#92400e',
                                  lineHeight: 1.15,
                                  marginBottom: 0,
                                }}
                              >
                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '3px' }}>
                                  <MdPerson size={gridIconSize(10)} />
                                  {onCall.employee.firstName} {onCall.employee.lastName}
                                </span>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleRemoveOnCall(dayOfWeek, onCall.employee.id);
                                  }}
                                  style={{
                                    background: 'none',
                                    border: 'none',
                                    color: '#d97706',
                                    cursor: 'pointer',
                                    fontSize: gridFontSize(12),
                                    padding: '0 3px',
                                  }}
                                >
                                  ✕
                                </button>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  }
                  if (row.key === 'notes') {
                    const noteContent = weeklyNotes[dayOfWeek]?.notes || '';
                    return (
                      <div
                        key={dayIndex}
                        onClick={() => {
                          setGridModalState({ row: 'notes', dayIndex });
                          setGridModalValue(noteContent);
                        }}
                        style={{
                          ...(colWidth ? { flex: `0 0 ${colWidth}px`, width: `${colWidth}px` } : { flex: 1, minWidth: '128px' }),
                          background: '#f9fce8',
                          borderRight: dayIndex < 6 ? '1px solid #d8e8a0' : 'none',
                          borderBottom: '1px solid #d8e8a0',
                          padding: '6px 8px',
                          minHeight: '40px',
                          cursor: 'pointer',
                          boxSizing: 'border-box',
                          ...(customHeight ? { height: `${customHeight}px`, overflowY: 'auto' as const } : {}),
                        }}
                      >
                        {!noteContent ? (
                          <em style={{ color: '#c8d6e5', fontSize: gridFontSize(11) }}>Nessuna nota</em>
                        ) : (
                          <div style={{ fontSize: gridFontSize(12), color: '#5a6b06', lineHeight: 1.3 }}>
                            {noteContent.split('\n').map((line, i) => (
                              <div key={i}>{line || ' '}</div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  }
                  if (row.key === 'ferie') {
                    const dayFerie = weeklyFerie[dayOfWeek] || [];
                    return (
                      <div
                        key={dayIndex}
                        onClick={() => { setFerieSelectedIds([]); setFerieAddState(dayOfWeek); }}
                        style={{
                          ...(colWidth ? { flex: `0 0 ${colWidth}px`, width: `${colWidth}px` } : { flex: 1, minWidth: '128px' }),
                          background: '#f5f3ff',
                          borderRight: dayIndex < 6 ? '1px solid #ddd6fe' : 'none',
                          borderBottom: '1px solid #ddd6fe',
                          padding: '6px 8px',
                          minHeight: '40px',
                          cursor: 'pointer',
                          boxSizing: 'border-box',
                          display: 'flex',
                          flexDirection: 'column',
                          justifyContent: 'flex-start',
                          gap: '4px',
                          ...(customHeight ? { height: `${customHeight}px`, overflowY: 'auto' as const } : {}),
                        }}
                      >
                        {dayFerie.length === 0 ? (
                          <em style={{ color: '#c8d6e5', fontSize: gridFontSize(10) }}>Clicca per aggiungere</em>
                        ) : (
                          <div>
                            {dayFerie.map(ferie => (
                              <div
                                key={ferie.id}
                                style={{
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  alignItems: 'center',
                                  fontSize: gridFontSize(11),
                                  color: '#5b21b6',
                                  lineHeight: 1.15,
                                  marginBottom: 0,
                                }}
                              >
                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '3px' }}>
                                  <MdPerson size={gridIconSize(10)} />
                                  {ferie.employee.firstName} {ferie.employee.lastName}
                                </span>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleRemoveFerie(dayOfWeek, ferie.employee.id);
                                  }}
                                  style={{
                                    background: 'none',
                                    border: 'none',
                                    color: '#7c3aed',
                                    cursor: 'pointer',
                                    fontSize: gridFontSize(12),
                                    padding: '0 3px',
                                  }}
                                >
                                  ✕
                                </button>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  }
                  return null;
                })}
              </Flex>
              <div
                onMouseDown={e => { e.preventDefault(); startRowResize(row.key, e.clientY); }}
                title="Trascina per ridimensionare la riga"
                style={{
                  position: 'absolute',
                  left: 0,
                  right: 0,
                  bottom: '-3px',
                  height: '7px',
                  cursor: 'row-resize',
                  zIndex: 2,
                  background: resizingRow === row.key ? 'rgba(95,132,202,0.35)' : 'transparent',
                }}
                className="weekly-grid-row-handle"
              />
              </Box>
            );
            })}
          </Box>
        </Box>
      </Box>

      {/* Statistics */}
      {stats && (
        <HStack spacing={6} px={4} py={6} className="no-print">
          <HStack>
            <Icon as={MdInfo} color="gray.400" />
            <Text fontSize="sm" color="gray.500">Totale interventi: <strong>{stats.totalTasks}</strong></Text>
          </HStack>
          <HStack>
            <Box w="10px" h="10px" borderRadius="full" bg="green.400" />
            <Text fontSize="sm">Completati: <strong>{stats.completedTasks}</strong></Text>
          </HStack>
          <HStack>
            <Box w="10px" h="10px" borderRadius="full" bg="orange.400" />
            <Text fontSize="sm">In corso: <strong>{stats.inProgressTasks}</strong></Text>
          </HStack>
          <HStack>
            <Box w="10px" h="10px" borderRadius="full" bg="blue.400" />
            <Text fontSize="sm">Da iniziare: <strong>{stats.toStartTasks}</strong></Text>
          </HStack>
        </HStack>
      )}

      {/* ── TASKS SECTION ──────────────────────────────────────────────────── */}

      {/* Filter box */}
      <Box
        px="10px"
        mx="10px"
        width={{ base: '100%', md: '96%' }}
        marginLeft={{ base: '0px', md: '0px' }}
        py="10px"
        backgroundColor="white"
        borderRadius="20px"
        boxShadow={shadowBox}
        mb="20px"
      >
        <Box p="15px">
          <Flex w="full" align="flex-end" flexWrap="wrap" gap={4}>
              <Box>
                <Text fontSize="sm" fontWeight="bold" mb={2}>Dipendente</Text>
                <Select
                  size="sm"
                  maxW="200px"
                  value={taskFilters.employeeId || ''}
                  onChange={e => handleTaskFilterChange({ employeeId: e.target.value || undefined })}
                >
                  <option value="">Tutti i dipendenti</option>
                  {employees.map(emp => (
                    <option key={emp.id} value={emp.id.toString()}>
                      {emp.firstName} {emp.lastName}
                    </option>
                  ))}
                </Select>
              </Box>
              <Box maxW="220px">
                <Text fontSize="sm" fontWeight="bold" mb={2}>Cliente</Text>
                <Flex gap={1} align="center">
                  <Box flex="1" minW={0}>
                    <CustomerSearchSelect
                      jwtToken={token}
                      onSessionExpired={() => keycloak?.logout()}
                      placeholder="Tutti i clienti"
                      value={taskFilters.customerId ? { id: taskFilters.customerId, fullName: customerNameCache[taskFilters.customerId] || '' } : null}
                      onChange={selectedCustomer => {
                        setCustomerNameCache(prev => ({ ...prev, [selectedCustomer.id]: selectedCustomer.fullName }));
                        handleTaskFilterChange({ customerId: selectedCustomer.id });
                      }}
                    />
                  </Box>
                  {taskFilters.customerId && (
                    <Tooltip label="Rimuovi filtro cliente" fontSize="md">
                      <IconButton
                        aria-label="Rimuovi filtro cliente"
                        icon={<Icon as={MdClose} />}
                        size="sm"
                        variant="ghost"
                        _focus={{ boxShadow: 'none' }}
                        onClick={() => handleTaskFilterChange({ customerId: undefined })}
                      />
                    </Tooltip>
                  )}
                </Flex>
              </Box>
              <Box>
                <Text fontSize="sm" fontWeight="bold" mb={2}>Data pianificata</Text>
                <HStack
                  spacing={1}
                  borderWidth="1px"
                  borderColor={borderColor}
                  borderRadius="6px"
                  px={2}
                  h="32px"
                >
                  <Input
                    type="date"
                    size="sm"
                    variant="unstyled"
                    maxW="115px"
                    value={taskFilters.dateFrom}
                    onChange={e => handleTaskFilterChange({ dateFrom: e.target.value })}
                  />
                  <Text color="gray.400" fontSize="sm">–</Text>
                  <Input
                    type="date"
                    size="sm"
                    variant="unstyled"
                    maxW="115px"
                    value={taskFilters.dateTo}
                    onChange={e => handleTaskFilterChange({ dateTo: e.target.value })}
                  />
                </HStack>
              </Box>
              <Box>
                <Text fontSize="sm" fontWeight="bold" mb={2}>Data inserimento</Text>
                <HStack
                  spacing={1}
                  borderWidth="1px"
                  borderColor={borderColor}
                  borderRadius="6px"
                  px={2}
                  h="32px"
                >
                  <Input
                    type="date"
                    size="sm"
                    variant="unstyled"
                    maxW="115px"
                    value={taskFilters.createdFrom}
                    onChange={e => handleTaskFilterChange({ createdFrom: e.target.value })}
                  />
                  <Text color="gray.400" fontSize="sm">–</Text>
                  <Input
                    type="date"
                    size="sm"
                    variant="unstyled"
                    maxW="115px"
                    value={taskFilters.createdTo}
                    onChange={e => handleTaskFilterChange({ createdTo: e.target.value })}
                  />
                </HStack>
              </Box>
              <Box>
                <Text fontSize="sm" fontWeight="bold" mb={2}>N. Rapporto</Text>
                <Input
                  type="text"
                  size="sm"
                  maxW="140px"
                  placeholder="Cerca numero..."
                  value={typedReportNumberFilter}
                  onChange={e => setTypedReportNumberFilter(e.target.value)}
                />
              </Box>
              <Box>
                <Text fontSize="sm" fontWeight="bold" mb={2}>Stato Rapporto</Text>
                <Select
                  size="sm"
                  maxW="160px"
                  value={taskFilters.reportStatus}
                  onChange={e => handleTaskFilterChange({ reportStatus: e.target.value })}
                >
                  <option value="">Tutti</option>
                  <option value="NONE">Non Compilato</option>
                  <option value="SUBMITTED">Compilato</option>
                  <option value="EDITED">Modificato</option>
                </Select>
              </Box>
          </Flex>
        </Box>
      </Box>

      {/* Table box */}
      <Box
        px="10px"
        mx="10px"
        width={{ base: '100%', md: '96%' }}
        marginLeft={{ base: '0px', md: '0px' }}
        mb={6}
        borderRadius="12px"
        boxShadow={shadowBox}
        bg="white"
        p={4}
      >
        <Flex px="25px" mb="20px" justifyContent="space-between" align="center">
          <Text color="#9FB50C" fontSize="22px" fontWeight="700" lineHeight="100%">
            Lista Interventi
          </Text>
          <Flex>
            {isAdmin && selectedTaskIds.length > 0 && (
              <Button
                onClick={onBulkDeleteOpen}
                _hover={{ bg: 'red.600' }}
                _active={{ bg: 'red.700' }}
                _focus={{ boxShadow: 'none' }}
                backgroundColor="red.500"
                color="white"
                style={{ height: '30px', padding: '0px 12px', borderRadius: '6px', border: 'none', cursor: 'pointer' }}
                fontSize="xs"
                mx="1"
              >
                Elimina selezionati ({selectedTaskIds.length})
              </Button>
            )}
            {isAdmin && (
              <Button
                onClick={() => { setForm(EMPTY_TASK_FORM); setAddressTouched(false); onCreateOpen(); }}
                _hover={{ bg: 'gray.700' }}
                _active={{ bg: 'gray.600' }}
                _focus={{ boxShadow: 'none' }}
                backgroundColor="#688CC8"
                color="white"
                style={{ height: '30px', padding: '0px 12px', borderRadius: '6px', border: 'none', cursor: 'pointer' }}
                fontSize="xs"
                mx="1"
              >
                + Aggiungi
              </Button>
            )}
            <Button onClick={fetchTasks} variant="ghost" colorScheme="blue" mx="1" p={2} _hover={{ bg: 'gray.200' }}>
              <Icon as={FaSync} w={5} h={5} />
            </Button>
          </Flex>
        </Flex>

        <Box overflowX="auto">
          <Table variant='simple' color='gray.500' mb='24px' mt="12px" w='100%' size="sm">
            <Thead>
              <Tr>
                {isAdmin && (
                  <Th pe='10px' borderColor={borderColor} paddingInline="0px" w="40px">
                    <Checkbox
                      isChecked={tasks.length > 0 && selectedTaskIds.length === tasks.length}
                      isIndeterminate={selectedTaskIds.length > 0 && selectedTaskIds.length < tasks.length}
                      onChange={toggleSelectAllTasks}
                    />
                  </Th>
                )}
                {['Cliente', 'Intervento', 'Dipendente', 'Data pianificata', 'Inizio', 'Fine', 'Stato', 'Rapp. d\'Intervento', 'Data Inserimento', 'Azioni'].map((label) => (
                  <Th key={label} pe='10px' borderColor={borderColor} paddingInline="0px">
                    <Flex
                      justifyContent='space-between'
                      align='center'
                      fontSize={{ sm: '10px', lg: '12px' }}
                      color='gray.400'>
                      {label}
                    </Flex>
                  </Th>
                ))}
              </Tr>
            </Thead>
            <Tbody>
              {taskLoading ? (
                Array.from({ length: 15 }).map((_, rowIndex) => (
                  <Tr key={`loading-row-${rowIndex}`}>
                    {Array.from({ length: isAdmin ? 11 : 10 }).map((_, columnIndex) => (
                      <Td
                        key={`loading-cell-${rowIndex}-${columnIndex}`}
                        pb='6px'
                        fontSize={{ sm: '14px' }}
                        minW={{ sm: '150px', md: '200px', lg: 'auto' }}
                        borderColor='transparent'
                        textAlign="center"
                      >
                        {rowIndex === 0 ? <Spinner size="sm" color="gray.500" /> : null}
                      </Td>
                    ))}
                  </Tr>
                ))
              ) : tasks.length > 0 ? (
                [...tasks].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()).map(task => {
                  return (
                    <Tr key={task.id} _hover={{ bg: 'gray.200' }}>
                      {isAdmin && (
                        <Td pb='2px' fontSize={{ sm: '14px' }} borderColor='transparent' paddingInline='0px'>
                          <Checkbox
                            isChecked={selectedTaskIds.includes(task.id)}
                            onChange={() => toggleSelectTask(task.id)}
                          />
                        </Td>
                      )}
                      <Td pb='2px' fontSize={{ sm: '14px' }} minW={{ sm: '150px', md: '200px', lg: 'auto' }} borderColor='transparent' paddingInline='0px'>
                        <Text fontSize="sm">{task.customer.fullName}</Text>
                      </Td>
                      <Td pb='2px' fontSize={{ sm: '14px' }} minW={{ sm: '150px', md: '200px', lg: 'auto' }} borderColor='transparent' paddingInline='0px'>
                        <VStack align="start" spacing={0}>
                          <Text fontWeight="bold" fontSize="sm">{task.title}</Text>
                          {task.address && (
                            <Link href={buildMapsUrl(task.address)} isExternal fontSize="xs" color="blue.500" textDecoration="underline">
                              {task.address}
                            </Link>
                          )}
                        </VStack>
                      </Td>
                      <Td pb='2px' fontSize={{ sm: '14px' }} minW={{ sm: '150px', md: '200px', lg: 'auto' }} borderColor='transparent' paddingInline='0px'>
                        {task.employees && task.employees.length > 0 ? (
                          <Text fontSize="sm">{task.employeesLabel}</Text>
                        ) : (
                          <Text fontSize="sm" color="gray.500">–</Text>
                        )}
                      </Td>
                      <Td pb='2px' fontSize={{ sm: '14px' }} minW={{ sm: '150px', md: '200px', lg: 'auto' }} borderColor='transparent' paddingInline='0px'>
                        {formatDateDDmmYYYY(task.scheduledDateTime)}<br />
                        {formatTime(task.scheduledDateTime)}
                      </Td>
                      <Td pb='2px' fontSize={{ sm: '14px' }} minW={{ sm: '150px', md: '200px', lg: 'auto' }} borderColor='transparent' paddingInline='0px'>
                        {task.effectiveStartDateTime ? (
                          <> {formatDateDDmmYYYY(task.effectiveStartDateTime)}<br /> {formatTime(task.effectiveStartDateTime)}</>
                        ) : '–'}
                      </Td>
                      <Td pb='2px' fontSize={{ sm: '14px' }} minW={{ sm: '150px', md: '200px', lg: 'auto' }} borderColor='transparent' paddingInline='0px'>
                        {task.effectiveEndDateTime ? (
                          <> {formatDateDDmmYYYY(task.effectiveEndDateTime)}<br /> {formatTime(task.effectiveEndDateTime)}</>
                        ) : '–'}
                      </Td>
                      <Td pb='2px' fontSize={{ sm: '14px' }} minW={{ sm: '150px', md: '200px', lg: 'auto' }} borderColor='transparent' paddingInline='0px'>
                        <Badge
                          colorScheme={task.status === 'COMPLETATO' ? 'green' : task.status === 'IN_CORSO' ? 'orange' : 'blue'}
                          cursor={task.status === 'COMPLETATO' ? 'default' : 'pointer'}
                          onClick={() => handleStatusClick(task)}
                          _hover={task.status !== 'COMPLETATO' ? { opacity: 0.8 } : {}}
                        >
                          {TASK_STATUS_LABELS[task.status]}
                        </Badge>
                      </Td>
                      <Td pb='2px' fontSize={{ sm: '14px' }} minW={{ sm: '150px', md: '200px', lg: 'auto' }} borderColor='transparent' paddingInline='0px'>
                        <Badge
                          colorScheme={task.hasInterventionReport ? 'green' : 'gray'}
                          cursor={task.hasInterventionReport ? 'pointer' : 'default'}
                          onClick={() => {
                            if (task.hasInterventionReport && task.interventionReportId) {
                              history.push(`/admin/rapporti-intervento/${task.interventionReportId}`);
                            }
                          }}
                          _hover={task.hasInterventionReport ? { opacity: 0.8 } : {}}
                        >
                          {task.hasInterventionReport
                            ? `N. ${task.interventionReportNumberFormatted}`
                            : 'Non Compilato'}
                        </Badge>
                      </Td>
                      <Td pb='2px' fontSize={{ sm: '14px' }} minW={{ sm: '150px', md: '200px', lg: 'auto' }} borderColor='transparent' paddingInline='0px'>
                        {formatDateDDmmYYYY(task.createdAt)}<br />
                        {formatTime(task.createdAt)}
                      </Td>
                      <Td pb='2px' fontSize={{ sm: '14px' }} minW={{ sm: '150px', md: '200px', lg: 'auto' }} borderColor='transparent' paddingInline='0px'>
                        {isAdmin && (
                          <TaskActionsMenu
                            onEdit={() => openEditModal(task)}
                            onCopy={() => handleCopyTask(task)}
                            onDelete={() => { setSelectedTask(task); setDeleteMode('ONLY_THIS'); onDeleteOpen(); }}
                          />
                        )}
                      </Td>
                    </Tr>
                  );
                })
              ) : (
                <Tr>
                  <Td colSpan={isAdmin ? 11 : 10} textAlign="center">
                    Nessun intervento
                  </Td>
                </Tr>
              )}
            </Tbody>
            <Tfoot w="100%">
              <Tr>
                <Td colSpan={isAdmin ? 11 : 10} textAlign="center">
                  <Flex justifyContent="center" alignItems="center" mt={4}>
                    <Button
                      onClick={handlePreviousTaskPage}
                      isDisabled={pagination.pageIndex === 0}
                      height="100%"
                    >
                      Precedente
                    </Button>
                    <Text mx={2}>
                      Pagina {pagination.pageIndex + 1} di {totalPages || 1}
                    </Text>
                    <Button
                      onClick={handleNextTaskPage}
                      isDisabled={pagination.pageIndex >= (totalPages || 1) - 1}
                      height="100%"
                    >
                      Successiva
                    </Button>
                    <Flex alignItems="center" mx={2}>
                      <Input
                        type="number"
                        placeholder="Pagina"
                        value={inputTaskPageNumber}
                        onChange={(e) => setInputTaskPageNumber(e.target.value)}
                        width="90px"
                        mx={2}
                      />
                      <Button
                        onClick={() => {
                          const pageNum = parseInt(inputTaskPageNumber, 10);
                          if (!isNaN(pageNum) && pageNum > 0 && pageNum <= (totalPages || 1)) {
                            setPagination(p => ({ ...p, pageIndex: pageNum - 1 }));
                            setInputTaskPageNumber('');
                          }
                        }}
                      >
                        Vai
                      </Button>
                    </Flex>
                  </Flex>
                </Td>
              </Tr>
            </Tfoot>
          </Table>
        </Box>
      </Box>

      {/* Modal: Aggiungi reperibile */}
      {onCallAddState && (
        <Modal isOpen={true} onClose={() => setOnCallAddState(null)} isCentered>
          <ModalOverlay />
          <ModalContent>
            <ModalHeader color="#9FB50C">Aggiungi Reperibile</ModalHeader>
            <ModalCloseButton _focus={{ boxShadow: 'none' }} />
            <ModalBody>
              <Text mb={4}>Seleziona uno o più dipendenti da aggiungere come reperibili:</Text>
              <ReactSelect
                isMulti
                closeMenuOnSelect={false}
                placeholder="Seleziona dipendenti..."
                noOptionsMessage={() => 'Nessun dipendente'}
                options={employees
                  .filter(emp => !weeklyOnCall[onCallAddState]?.some(oc => oc.employee.id === emp.id))
                  .map(emp => ({ value: emp.id, label: `${emp.firstName} ${emp.lastName}` }))}
                value={employees
                  .filter(emp => onCallSelectedIds.includes(emp.id))
                  .map(emp => ({ value: emp.id, label: `${emp.firstName} ${emp.lastName}` }))}
                onChange={(selected: MultiValue<{ value: number; label: string }>) =>
                  setOnCallSelectedIds(selected.map(s => s.value))
                }
                styles={{
                  control: base => ({ ...base, minHeight: '40px', borderColor: '#E2E8F0', borderRadius: '6px' }),
                  multiValue: base => ({ ...base, backgroundColor: '#688CC820' }),
                  multiValueLabel: base => ({ ...base, color: '#1a2942', fontSize: '13px' }),
                }}
              />
            </ModalBody>
            <ModalFooter>
              <Button colorScheme="gray" mr={3} onClick={() => setOnCallAddState(null)}>Annulla</Button>
              <Button
                backgroundColor="#688CC8" color="white"
                _hover={{ bg: 'gray.700' }} _active={{ bg: 'gray.600' }} _focus={{ boxShadow: 'none' }}
                isDisabled={onCallSelectedIds.length === 0}
                onClick={() => handleAddOnCall(onCallSelectedIds)}
              >
                Salva
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      )}

      {/* Modal: Aggiungi ferie */}
      {ferieAddState && (
        <Modal isOpen={true} onClose={() => setFerieAddState(null)} isCentered>
          <ModalOverlay />
          <ModalContent>
            <ModalHeader color="#9FB50C">Aggiungi Ferie</ModalHeader>
            <ModalCloseButton _focus={{ boxShadow: 'none' }} />
            <ModalBody>
              <Text mb={4}>Seleziona uno o più dipendenti da aggiungere come in ferie:</Text>
              <ReactSelect
                isMulti
                closeMenuOnSelect={false}
                placeholder="Seleziona dipendenti..."
                noOptionsMessage={() => 'Nessun dipendente'}
                options={employees
                  .filter(emp => !weeklyFerie[ferieAddState]?.some(f => f.employee.id === emp.id))
                  .map(emp => ({ value: emp.id, label: `${emp.firstName} ${emp.lastName}` }))}
                value={employees
                  .filter(emp => ferieSelectedIds.includes(emp.id))
                  .map(emp => ({ value: emp.id, label: `${emp.firstName} ${emp.lastName}` }))}
                onChange={(selected: MultiValue<{ value: number; label: string }>) =>
                  setFerieSelectedIds(selected.map(s => s.value))
                }
                styles={{
                  control: base => ({ ...base, minHeight: '40px', borderColor: '#E2E8F0', borderRadius: '6px' }),
                  multiValue: base => ({ ...base, backgroundColor: '#688CC820' }),
                  multiValueLabel: base => ({ ...base, color: '#1a2942', fontSize: '13px' }),
                }}
              />
            </ModalBody>
            <ModalFooter>
              <Button colorScheme="gray" mr={3} onClick={() => setFerieAddState(null)}>Annulla</Button>
              <Button
                backgroundColor="#688CC8" color="white"
                _hover={{ bg: 'gray.700' }} _active={{ bg: 'gray.600' }} _focus={{ boxShadow: 'none' }}
                isDisabled={ferieSelectedIds.length === 0}
                onClick={() => handleAddFerie(ferieSelectedIds)}
              >
                Salva
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      )}

      {/* ── MODALS: TASKS ──────────────────────────────────────────────────── */}

      {/* Create task */}
      {isAdmin && (
        <Modal isOpen={isCreateOpen} onClose={handleCreateClose} size="2xl">
          <ModalOverlay />
          <ModalContent>
            <ModalHeader color="#9FB50C" fontSize="xl" fontWeight="bold">Nuovo Intervento</ModalHeader>
            <ModalCloseButton _focus={{ boxShadow: 'none' }} />
            <ModalBody>
              <VStack spacing={3}>
                <Flex gap={3} w="full" direction={{ base: 'column', md: 'row' }}>
                  <FormControl isRequired flex="1">
                    <FormLabel>Titolo</FormLabel>
                    <Input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} _focus={{ boxShadow: 'black' }} />
                  </FormControl>
                  <FormControl isRequired flex="1">
                    <FormLabel>Tipo intervento</FormLabel>
                    <Flex gap={2}>
                      <Select flex="1" value={form.taskType} onChange={e => setForm(f => ({ ...f, taskType: e.target.value as any }))}>
                        <option value="">Seleziona tipo intervento</option>
                        {operationTypes.map(t => (
                          <option key={t.id} value={t.name}>{t.name}</option>
                        ))}
                      </Select>
                      <Tooltip label="Aggiungi nuova tipologia" fontSize="md">
                        <Button
                          onClick={() => openAddTipologyModal('create')}
                          _hover={{ bg: 'gray.700' }} _active={{ bg: 'gray.600' }} _focus={{ boxShadow: 'none' }}
                          backgroundColor="#688CC8"
                          color="white"
                          style={{ height: '40px', padding: '0px 10px', borderRadius: '6px', border: 'none', cursor: 'pointer' }}
                        >
                          <Icon as={MdAdd} w={5} h={5} />
                        </Button>
                      </Tooltip>
                    </Flex>
                  </FormControl>
                </Flex>
                <Flex gap={3} w="full" direction={{ base: 'column', md: 'row' }}>
                  <FormControl isRequired flex="1">
                    <FormLabel>Cliente</FormLabel>
                    <Flex gap={2}>
                      <Box flex="1">
                        <CustomerSearchSelect
                          jwtToken={token}
                          onSessionExpired={() => keycloak?.logout()}
                          value={form.customerId ? { id: form.customerId, fullName: customerNameCache[form.customerId] || '' } : null}
                          onChange={selectedCustomer => {
                            setCustomerNameCache(prev => ({ ...prev, [selectedCustomer.id]: selectedCustomer.fullName }));
                            const customerAddress = buildCustomerAddressString(selectedCustomer);
                            setForm(f => ({
                              ...f,
                              customerId: selectedCustomer.id,
                              address: customerAddress || f.address,
                            }));
                            setAddressTouched(false);
                          }}
                        />
                      </Box>
                      <Tooltip label="Aggiungi nuovo cliente" fontSize="md">
                        <Button
                          onClick={() => openAddCustomerModal('create')}
                          _hover={{ bg: 'gray.700' }} _active={{ bg: 'gray.600' }} _focus={{ boxShadow: 'none' }}
                          backgroundColor="#688CC8"
                          color="white"
                          style={{ height: '40px', padding: '0px 10px', borderRadius: '6px', border: 'none', cursor: 'pointer' }}
                        >
                          <Icon as={MdAdd} w={5} h={5} />
                        </Button>
                      </Tooltip>
                    </Flex>
                  </FormControl>
                  <FormControl flex="1">
                    <FormLabel>Dipendenti <Text as="span" color="red.500">*</Text></FormLabel>
                    <ReactSelect
                      isMulti
                      closeMenuOnSelect={false}
                      placeholder="Seleziona dipendenti..."
                      noOptionsMessage={() => 'Nessun dipendente'}
                      options={employees.map(emp => ({ value: emp.id, label: `${emp.firstName} ${emp.lastName}` }))}
                      value={employees
                        .filter(emp => form.employeeIds.includes(emp.id))
                        .map(emp => ({ value: emp.id, label: `${emp.firstName} ${emp.lastName}` }))}
                      onChange={(selected: MultiValue<{ value: number; label: string }>) =>
                        setForm(f => ({ ...f, employeeIds: selected.map(s => s.value) }))
                      }
                      styles={{
                        control: base => ({ ...base, minHeight: '40px', borderColor: '#E2E8F0', borderRadius: '6px' }),
                        multiValue: base => ({ ...base, backgroundColor: '#688CC820' }),
                        multiValueLabel: base => ({ ...base, color: '#1a2942', fontSize: '13px' }),
                      }}
                    />
                  </FormControl>
                </Flex>
                <FormControl>
                  <FormLabel>Mezzi</FormLabel>
                  <ReactSelect
                    isMulti
                    closeMenuOnSelect={false}
                    placeholder="Seleziona mezzi..."
                    noOptionsMessage={() => 'Nessun mezzo'}
                    options={vehicles.map(v => ({ value: v.id, label: v.name }))}
                    value={vehicles
                      .filter(v => (form.vehicleIds || []).includes(v.id))
                      .map(v => ({ value: v.id, label: v.name }))}
                    onChange={(selected: MultiValue<{ value: number; label: string }>) =>
                      setForm(f => ({ ...f, vehicleIds: selected.map(s => s.value) }))
                    }
                    styles={{
                      control: base => ({ ...base, minHeight: '40px', borderColor: '#E2E8F0', borderRadius: '6px' }),
                      multiValue: base => ({ ...base, backgroundColor: '#688CC820' }),
                      multiValueLabel: base => ({ ...base, color: '#1a2942', fontSize: '13px' }),
                    }}
                  />
                </FormControl>
                <FormControl isRequired>
                  <FormLabel>Data e ora pianificata</FormLabel>
                  <Input type="datetime-local" value={form.scheduledDateTime} onChange={e => setForm(f => ({ ...f, scheduledDateTime: e.target.value }))} _focus={{ boxShadow: 'black' }} />
                </FormControl>
                <FormControl isInvalid={addressTouched && !validateAddress(form.address || '')}>
                  <FormLabel>Indirizzo <Text as="span" color="red.500">*</Text></FormLabel>
                  <Input
                    placeholder="Es: Via Roma 10, 70032 Bitonto (BA)"
                    value={form.address || ''}
                    onChange={e => setForm(f => ({ ...f, address: e.target.value }))}
                    onBlur={() => setAddressTouched(true)}
                    _focus={{ boxShadow: 'black' }}
                  />
                  {addressTouched && !validateAddress(form.address || '') ? (
                    <FormErrorMessage>
                      {(form.address || '').trim() === ''
                        ? "L'indirizzo è obbligatorio per salvare l'intervento."
                        : 'Inserisci un indirizzo completo (via, numero civico, CAP, città) oppure un link Google Maps valido.'}
                    </FormErrorMessage>
                  ) : (
                    <FormHelperText fontSize="xs" color="gray.500">
                      Inserisci via, numero civico, CAP e città — oppure incolla un link Google Maps
                    </FormHelperText>
                  )}
                </FormControl>
                <FormControl>
                  <FormLabel>Descrizione</FormLabel>
                  <Textarea value={form.description || ''} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} rows={2} _focus={{ boxShadow: 'black' }} />
                </FormControl>
                <Flex gap={3} w="full" align="flex-start" direction={{ base: 'column', md: 'row' }}>
                  <FormControl flex={form.isRecurring ? '1' : undefined}>
                    <FormLabel>Ricorrente?</FormLabel>
                    <Switch
                      isChecked={form.isRecurring}
                      onChange={e => setForm(f => ({
                        ...f,
                        isRecurring: e.target.checked,
                        recurrenceType: e.target.checked ? 'DAILY' : undefined,
                        recurrenceInterval: e.target.checked ? 1 : undefined,
                      }))}
                    />
                  </FormControl>
                  {form.isRecurring && (
                    <>
                      <FormControl isRequired flex="1">
                        <FormLabel>Ripeti ogni</FormLabel>
                        <Flex gap="2">
                          <Input
                            type="text"
                            width="80px"
                            value={form.recurrenceInterval !== undefined ? String(form.recurrenceInterval) : ''}
                            onChange={(e) => {
                              const value = e.target.value.trim();
                              if (/^\d*$/.test(value) && (value === '' || parseInt(value, 10) > 0)) {
                                setForm(f => ({ ...f, recurrenceInterval: value === '' ? undefined : parseInt(value, 10) }));
                              }
                            }}
                            onBlur={() => {
                              if (!form.recurrenceInterval || form.recurrenceInterval <= 0) {
                                setForm(f => ({ ...f, recurrenceInterval: 1 }));
                              }
                            }}
                            _focus={{ boxShadow: 'black' }}
                          />
                          <Menu>
                            <MenuButton as={Button} rightIcon={<ChevronDownIcon />} bg="gray.200">
                              {form.recurrenceType ? RECURRENCE_TYPE_LABELS[form.recurrenceType] : 'Seleziona tipo'}
                            </MenuButton>
                            <MenuList>
                              {Object.entries(RECURRENCE_TYPE_LABELS).map(([val, label]) => (
                                <MenuItem key={val} onClick={() => setForm(f => ({ ...f, recurrenceType: val as any }))}>{label}</MenuItem>
                              ))}
                            </MenuList>
                          </Menu>
                        </Flex>
                      </FormControl>
                      <FormControl flex="1">
                        <FormLabel>Data fine ricorrenza</FormLabel>
                        <Input type="date" value={form.recurrenceEndDate || ''} onChange={e => setForm(f => ({ ...f, recurrenceEndDate: e.target.value || undefined }))} _focus={{ boxShadow: 'black' }} />
                        <FormHelperText fontSize="xs" color="gray.500">
                          Se vuoto, termina il 31/12/{new Date().getFullYear()}.
                        </FormHelperText>
                      </FormControl>
                    </>
                  )}
                </Flex>
              </VStack>
            </ModalBody>
            <ModalFooter>
              <Button variant="ghost" mr={3} onClick={handleCreateClose}>Annulla</Button>
              <Tooltip
                label={getTaskFormDisabledReason(form)}
                isDisabled={!getTaskFormDisabledReason(form)}
                shouldWrapChildren
              >
                <Button
                  backgroundColor="#688CC8"
                  color="white"
                  onClick={handleCreateTask}
                  _hover={{ bg: 'gray.700' }}
                  _active={{ bg: 'gray.600' }}
                  _focus={{ boxShadow: 'none' }}
                  isDisabled={!!getTaskFormDisabledReason(form)}
                >
                  Salva
                </Button>
              </Tooltip>
            </ModalFooter>
          </ModalContent>
        </Modal>
      )}

      {/* Edit task */}
      {isAdmin && (
        <Modal isOpen={isEditOpen} onClose={handleEditClose} size="2xl">
          <ModalOverlay />
          <ModalContent>
            <ModalHeader color="#9FB50C" fontSize="xl" fontWeight="bold">Modifica Intervento</ModalHeader>
            <ModalCloseButton _focus={{ boxShadow: 'none' }} />
            <ModalBody>
              <VStack spacing={3}>
                <Flex gap={3} w="full" direction={{ base: 'column', md: 'row' }}>
                  <FormControl isRequired flex="1">
                    <FormLabel>Titolo</FormLabel>
                    <Input value={editForm.title} onChange={e => setEditForm(f => ({ ...f, title: e.target.value }))} _focus={{ boxShadow: 'black' }} />
                  </FormControl>
                  <FormControl isRequired flex="1">
                    <FormLabel>Tipo intervento</FormLabel>
                    <Flex gap={2}>
                      <Select flex="1" value={editForm.taskType} onChange={e => setEditForm(f => ({ ...f, taskType: e.target.value as any }))}>
                        <option value="">Seleziona tipo intervento</option>
                        {operationTypes.map(t => (
                          <option key={t.id} value={t.name}>{t.name}</option>
                        ))}
                      </Select>
                      <Tooltip label="Aggiungi nuova tipologia" fontSize="md">
                        <Button
                          onClick={() => openAddTipologyModal('edit')}
                          _hover={{ bg: 'gray.700' }} _active={{ bg: 'gray.600' }} _focus={{ boxShadow: 'none' }}
                          backgroundColor="#688CC8"
                          color="white"
                          style={{ height: '40px', padding: '0px 10px', borderRadius: '6px', border: 'none', cursor: 'pointer' }}
                        >
                          <Icon as={MdAdd} w={5} h={5} />
                        </Button>
                      </Tooltip>
                    </Flex>
                  </FormControl>
                </Flex>
                <Flex gap={3} w="full" direction={{ base: 'column', md: 'row' }}>
                  <FormControl isRequired flex="1">
                    <FormLabel>Cliente</FormLabel>
                    <Flex gap={2}>
                      <Box flex="1">
                        <CustomerSearchSelect
                          jwtToken={token}
                          onSessionExpired={() => keycloak?.logout()}
                          value={editForm.customerId ? { id: editForm.customerId, fullName: customerNameCache[editForm.customerId] || '' } : null}
                          onChange={selectedCustomer => {
                            setCustomerNameCache(prev => ({ ...prev, [selectedCustomer.id]: selectedCustomer.fullName }));
                            const customerAddress = buildCustomerAddressString(selectedCustomer);
                            setEditForm(f => ({
                              ...f,
                              customerId: selectedCustomer.id,
                              address: customerAddress || f.address,
                            }));
                            setEditAddressTouched(false);
                          }}
                        />
                      </Box>
                      <Tooltip label="Aggiungi nuovo cliente" fontSize="md">
                        <Button
                          onClick={() => openAddCustomerModal('edit')}
                          _hover={{ bg: 'gray.700' }} _active={{ bg: 'gray.600' }} _focus={{ boxShadow: 'none' }}
                          backgroundColor="#688CC8"
                          color="white"
                          style={{ height: '40px', padding: '0px 10px', borderRadius: '6px', border: 'none', cursor: 'pointer' }}
                        >
                          <Icon as={MdAdd} w={5} h={5} />
                        </Button>
                      </Tooltip>
                    </Flex>
                  </FormControl>
                  <FormControl flex="1">
                    <FormLabel>Dipendenti <Text as="span" color="red.500">*</Text></FormLabel>
                    <ReactSelect
                      isMulti
                      closeMenuOnSelect={false}
                      placeholder="Seleziona dipendenti..."
                      noOptionsMessage={() => 'Nessun dipendente'}
                      options={employees.map(emp => ({ value: emp.id, label: `${emp.firstName} ${emp.lastName}` }))}
                      value={employees
                        .filter(emp => editForm.employeeIds.includes(emp.id))
                        .map(emp => ({ value: emp.id, label: `${emp.firstName} ${emp.lastName}` }))}
                      onChange={(selected: MultiValue<{ value: number; label: string }>) =>
                        setEditForm(f => ({ ...f, employeeIds: selected.map(s => s.value) }))
                      }
                      styles={{
                        control: base => ({ ...base, minHeight: '40px', borderColor: '#E2E8F0', borderRadius: '6px' }),
                        multiValue: base => ({ ...base, backgroundColor: '#688CC820' }),
                        multiValueLabel: base => ({ ...base, color: '#1a2942', fontSize: '13px' }),
                      }}
                    />
                  </FormControl>
                </Flex>
                <FormControl>
                  <FormLabel>Mezzi</FormLabel>
                  <ReactSelect
                    isMulti
                    closeMenuOnSelect={false}
                    placeholder="Seleziona mezzi..."
                    noOptionsMessage={() => 'Nessun mezzo'}
                    options={vehicles.map(v => ({ value: v.id, label: v.name }))}
                    value={vehicles
                      .filter(v => (editForm.vehicleIds || []).includes(v.id))
                      .map(v => ({ value: v.id, label: v.name }))}
                    onChange={(selected: MultiValue<{ value: number; label: string }>) =>
                      setEditForm(f => ({ ...f, vehicleIds: selected.map(s => s.value) }))
                    }
                    styles={{
                      control: base => ({ ...base, minHeight: '40px', borderColor: '#E2E8F0', borderRadius: '6px' }),
                      multiValue: base => ({ ...base, backgroundColor: '#688CC820' }),
                      multiValueLabel: base => ({ ...base, color: '#1a2942', fontSize: '13px' }),
                    }}
                  />
                </FormControl>
                <FormControl isRequired>
                  <FormLabel>Data e ora pianificata</FormLabel>
                  <Input type="datetime-local" value={editForm.scheduledDateTime} onChange={e => setEditForm(f => ({ ...f, scheduledDateTime: e.target.value }))} _focus={{ boxShadow: 'black' }} />
                </FormControl>
                <FormControl isInvalid={editAddressTouched && !validateAddress(editForm.address || '')}>
                  <FormLabel>Indirizzo <Text as="span" color="red.500">*</Text></FormLabel>
                  <Input
                    placeholder="Es: Via Roma 10, 70032 Bitonto (BA)"
                    value={editForm.address || ''}
                    onChange={e => setEditForm(f => ({ ...f, address: e.target.value }))}
                    onBlur={() => setEditAddressTouched(true)}
                    _focus={{ boxShadow: 'black' }}
                  />
                  {editAddressTouched && !validateAddress(editForm.address || '') ? (
                    <FormErrorMessage>
                      {(editForm.address || '').trim() === ''
                        ? "L'indirizzo è obbligatorio per salvare l'intervento."
                        : 'Inserisci un indirizzo completo (via, numero civico, CAP, città) oppure un link Google Maps valido.'}
                    </FormErrorMessage>
                  ) : (
                    <FormHelperText fontSize="xs" color="gray.500">
                      Inserisci via, numero civico, CAP e città — oppure incolla un link Google Maps
                    </FormHelperText>
                  )}
                </FormControl>
                <FormControl>
                  <FormLabel>Descrizione</FormLabel>
                  <Textarea value={editForm.description || ''} onChange={e => setEditForm(f => ({ ...f, description: e.target.value }))} rows={2} _focus={{ boxShadow: 'black' }} />
                </FormControl>
                <Flex gap={3} w="full" align="flex-start" direction={{ base: 'column', md: 'row' }}>
                  <FormControl flex={editForm.isRecurring ? '1' : undefined}>
                    <FormLabel>Ricorrente?</FormLabel>
                    <Switch
                      isChecked={editForm.isRecurring}
                      onChange={e => setEditForm(f => ({
                        ...f,
                        isRecurring: e.target.checked,
                        recurrenceType: e.target.checked ? (f.recurrenceType ?? 'DAILY') : undefined,
                        recurrenceInterval: e.target.checked ? (f.recurrenceInterval ?? 1) : undefined,
                      }))}
                    />
                  </FormControl>
                  {editForm.isRecurring && (
                    <>
                      <FormControl isRequired flex="1">
                        <FormLabel>Ripeti ogni</FormLabel>
                        <Flex gap="2">
                          <Input
                            type="text"
                            width="80px"
                            value={editForm.recurrenceInterval !== undefined ? String(editForm.recurrenceInterval) : ''}
                            onChange={(e) => {
                              const value = e.target.value.trim();
                              if (/^\d*$/.test(value) && (value === '' || parseInt(value, 10) > 0)) {
                                setEditForm(f => ({ ...f, recurrenceInterval: value === '' ? undefined : parseInt(value, 10) }));
                              }
                            }}
                            onBlur={() => {
                              if (!editForm.recurrenceInterval || editForm.recurrenceInterval <= 0) {
                                setEditForm(f => ({ ...f, recurrenceInterval: 1 }));
                              }
                            }}
                            _focus={{ boxShadow: 'black' }}
                          />
                          <Menu>
                            <MenuButton as={Button} rightIcon={<ChevronDownIcon />} bg="gray.200">
                              {editForm.recurrenceType ? RECURRENCE_TYPE_LABELS[editForm.recurrenceType] : 'Seleziona tipo'}
                            </MenuButton>
                            <MenuList>
                              {Object.entries(RECURRENCE_TYPE_LABELS).map(([val, label]) => (
                                <MenuItem key={val} onClick={() => setEditForm(f => ({ ...f, recurrenceType: val as any }))}>{label}</MenuItem>
                              ))}
                            </MenuList>
                          </Menu>
                        </Flex>
                      </FormControl>
                      <FormControl flex="1">
                        <FormLabel>Data fine ricorrenza</FormLabel>
                        <Input type="date" value={editForm.recurrenceEndDate || ''} onChange={e => setEditForm(f => ({ ...f, recurrenceEndDate: e.target.value || undefined }))} _focus={{ boxShadow: 'black' }} />
                        <FormHelperText fontSize="xs" color="gray.500">
                          Se vuoto, termina il 31/12/{new Date().getFullYear()}.
                        </FormHelperText>
                      </FormControl>
                    </>
                  )}
                </Flex>
              </VStack>
            </ModalBody>
            <ModalFooter justifyContent="space-between">
              {isAdmin && editTask ? (
                <Button
                  colorScheme="red"
                  variant="outline"
                  onClick={() => {
                    const task = editTask;
                    handleEditClose();
                    setSelectedTask(task);
                    setDeleteMode('ONLY_THIS');
                    onDeleteOpen();
                  }}
                >
                  Elimina
                </Button>
              ) : (
                <Box />
              )}
              <Flex>
                <Button variant="ghost" mr={3} onClick={handleEditClose}>Annulla</Button>
                <Tooltip
                  label={getTaskFormDisabledReason(editForm)}
                  isDisabled={!getTaskFormDisabledReason(editForm)}
                  shouldWrapChildren
                >
                  <Button
                    backgroundColor="#688CC8"
                    color="white"
                    onClick={handleUpdateTask}
                    _hover={{ bg: 'gray.700' }}
                    _active={{ bg: 'gray.600' }}
                    _focus={{ boxShadow: 'none' }}
                    isDisabled={!!getTaskFormDisabledReason(editForm)}
                  >
                    Salva
                  </Button>
                </Tooltip>
              </Flex>
            </ModalFooter>
          </ModalContent>
        </Modal>
      )}

      {/* Delete task */}
      {isAdmin && selectedTask && (
        <>
          {!selectedTask.isRecurring && selectedTask.parentTaskId === null ? (
            <AlertDialog isOpen={isDeleteOpen} leastDestructiveRef={deleteAlertCancelRef} onClose={onDeleteClose}>
              <AlertDialogOverlay>
                <AlertDialogContent>
                  <AlertDialogHeader fontSize="lg" fontWeight="bold">Elimina intervento</AlertDialogHeader>
                  <AlertDialogBody>
                    Sei sicuro di voler eliminare il intervento "{selectedTask.title}"?
                  </AlertDialogBody>
                  <AlertDialogFooter>
                    <Button ref={deleteAlertCancelRef} onClick={onDeleteClose}>Annulla</Button>
                    <Button colorScheme="red" onClick={handleDeleteTask} ml={3}>Elimina</Button>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialogOverlay>
            </AlertDialog>
          ) : (
            <Modal isOpen={isDeleteOpen} onClose={onDeleteClose}>
              <ModalOverlay />
              <ModalContent>
                <ModalHeader color="#9FB50C">Elimina intervento ricorrente</ModalHeader>
                <ModalCloseButton _focus={{ boxShadow: 'none' }} />
                <ModalBody>
                  <Text mb={4}>Come desideri eliminare questo intervento?</Text>
                  <RadioGroup value={deleteMode} onChange={v => setDeleteMode(v as DeleteMode)}>
                    <VStack align="start" spacing={3}>
                      {Object.entries(DELETE_MODE_LABELS).map(([val, label]) => (
                        <Radio key={val} value={val}>{label}</Radio>
                      ))}
                    </VStack>
                  </RadioGroup>
                </ModalBody>
                <ModalFooter>
                  <Button colorScheme="red" onClick={handleDeleteTask} mr={3}>Elimina</Button>
                  <Button variant="ghost" onClick={onDeleteClose}>Annulla</Button>
                </ModalFooter>
              </ModalContent>
            </Modal>
          )}
        </>
      )}

      {/* Bulk delete tasks */}
      {isAdmin && (
        <AlertDialog isOpen={isBulkDeleteOpen} leastDestructiveRef={bulkDeleteAlertCancelRef} onClose={onBulkDeleteClose}>
          <AlertDialogOverlay>
            <AlertDialogContent>
              <AlertDialogHeader fontSize="lg" fontWeight="bold">Elimina interventi selezionati</AlertDialogHeader>
              <AlertDialogBody>
                Sei sicuro di voler eliminare {selectedTaskIds.length} intervento/i selezionato/i? L'operazione non è reversibile.
              </AlertDialogBody>
              <AlertDialogFooter>
                <Button ref={bulkDeleteAlertCancelRef} onClick={onBulkDeleteClose}>Annulla</Button>
                <Button colorScheme="red" onClick={handleBulkDeleteTasks} ml={3} isLoading={isBulkDeleting}>Elimina</Button>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialogOverlay>
        </AlertDialog>
      )}

      {/* Add tipologia intervento (inline, dalla modale nuovo/modifica intervento) */}
      <Modal isOpen={isAddTipologyOpen} onClose={onAddTipologyClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader color="#9FB50C">Nuova Tipologia Intervento</ModalHeader>
          <ModalCloseButton _focus={{ boxShadow: 'none' }} />
          <ModalBody>
            <FormControl>
              <FormLabel>Nome <Text as="span" color="red.500">*</Text></FormLabel>
              <Input
                _focus={{ boxShadow: 'black' }}
                placeholder="Nome tipologia..."
                value={newTipologyName}
                onChange={(e) => setNewTipologyName(e.target.value)}
              />
            </FormControl>
          </ModalBody>
          <ModalFooter>
            <Button
              backgroundColor="#688CC8"
              color="white"
              mr={3}
              onClick={handleSaveNewTipology}
              isLoading={isSavingTipology}
              isDisabled={!newTipologyName.trim()}
              _hover={{ bg: 'gray.700' }}
              _active={{ bg: 'gray.600' }}
              _focus={{ boxShadow: 'none' }}
            >
              Salva
            </Button>
            <Button colorScheme="gray" onClick={onAddTipologyClose}>Annulla</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Add cliente (inline, dalla modale nuovo/modifica intervento) */}
      <Modal isOpen={isAddCustomerOpen} onClose={onAddCustomerClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader color="#9FB50C">Nuovo Cliente</ModalHeader>
          <ModalCloseButton _focus={{ boxShadow: 'none' }} />
          <ModalBody>
            <VStack spacing={3} align="stretch">
              <FormControl isRequired>
                <FormLabel>Ragione Sociale</FormLabel>
                <Input
                  _focus={{ boxShadow: 'black' }}
                  placeholder="Ragione Sociale"
                  value={newCustomerForm.fullName}
                  onChange={e => setNewCustomerForm(f => ({ ...f, fullName: e.target.value }))}
                />
              </FormControl>
              <Flex gap={3} direction={{ base: 'column', md: 'row' }}>
                <FormControl flex="1">
                  <FormLabel>P. IVA</FormLabel>
                  <Input
                    _focus={{ boxShadow: 'black' }}
                    placeholder="P. IVA"
                    value={newCustomerForm.vatNumber}
                    onChange={e => setNewCustomerForm(f => ({ ...f, vatNumber: e.target.value }))}
                    maxLength={11}
                    inputMode="numeric"
                    pattern="\d*"
                  />
                </FormControl>
                <FormControl flex="1">
                  <FormLabel>Codice Fiscale</FormLabel>
                  <Input
                    _focus={{ boxShadow: 'black' }}
                    placeholder="Codice Fiscale"
                    value={newCustomerForm.taxCode}
                    onChange={e => setNewCustomerForm(f => ({ ...f, taxCode: e.target.value }))}
                    maxLength={16}
                  />
                </FormControl>
              </Flex>
              <Flex gap={3} direction={{ base: 'column', md: 'row' }}>
                <FormControl flex="1">
                  <FormLabel>Email</FormLabel>
                  <Input
                    _focus={{ boxShadow: 'black' }}
                    placeholder="Email"
                    value={newCustomerForm.email}
                    onChange={e => setNewCustomerForm(f => ({ ...f, email: e.target.value }))}
                  />
                </FormControl>
                <FormControl flex="1">
                  <FormLabel>Telefono</FormLabel>
                  <Input
                    _focus={{ boxShadow: 'black' }}
                    placeholder="Telefono"
                    value={newCustomerForm.phone}
                    onChange={e => setNewCustomerForm(f => ({ ...f, phone: e.target.value }))}
                  />
                </FormControl>
              </Flex>
              <FormControl>
                <FormLabel>Indirizzo</FormLabel>
                <Input
                  _focus={{ boxShadow: 'black' }}
                  placeholder="Indirizzo"
                  value={newCustomerForm.address}
                  onChange={e => setNewCustomerForm(f => ({ ...f, address: e.target.value }))}
                />
              </FormControl>
              <Flex gap={3}>
                <Box flex="2" position="relative">
                  <FormControl>
                    <FormLabel>Città</FormLabel>
                    <Input
                      _focus={{ boxShadow: 'black' }}
                      placeholder="Città"
                      value={newCustomerForm.addressCity}
                      onChange={e => setNewCustomerForm(f => ({ ...f, addressCity: e.target.value }))}
                      onBlur={handleNewCustomerCityBlur}
                    />
                  </FormControl>
                  {isLookingUpCustomerCity && (
                    <Spinner size="xs" position="absolute" right="10px" top="38px" />
                  )}
                </Box>
                <FormControl flex="1">
                  <FormLabel>Provincia</FormLabel>
                  <Input
                    _focus={{ boxShadow: 'black' }}
                    placeholder="Provincia"
                    value={newCustomerForm.addressState}
                    onChange={e => setNewCustomerForm(f => ({ ...f, addressState: e.target.value }))}
                    maxLength={2}
                  />
                </FormControl>
                <FormControl flex="1">
                  <FormLabel>CAP</FormLabel>
                  <Input
                    _focus={{ boxShadow: 'black' }}
                    placeholder="CAP"
                    value={newCustomerForm.addressZip}
                    onChange={e => setNewCustomerForm(f => ({ ...f, addressZip: e.target.value }))}
                  />
                </FormControl>
              </Flex>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Tooltip label="Inserisci una ragione sociale per salvare" isDisabled={!!newCustomerForm.fullName.trim()} shouldWrapChildren>
              <Button
                backgroundColor="#688CC8"
                color="white"
                mr={3}
                onClick={handleSaveNewCustomer}
                isLoading={isSavingCustomer}
                isDisabled={!newCustomerForm.fullName.trim()}
                _hover={{ bg: 'gray.700' }}
                _active={{ bg: 'gray.600' }}
                _focus={{ boxShadow: 'none' }}
              >
                Salva
              </Button>
            </Tooltip>
            <Button colorScheme="gray" onClick={onAddCustomerClose}>Annulla</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Status change */}
      {pendingStatusTask && (
        <AlertDialog isOpen={isStatusOpen} leastDestructiveRef={statusAlertCancelRef} onClose={onStatusClose}>
          <AlertDialogOverlay>
            <AlertDialogContent>
              <AlertDialogHeader fontSize="lg" fontWeight="bold">
                {pendingStatusTask.status === 'DA_INIZIARE' ? 'Avviare il intervento?' : 'Completare il intervento?'}
              </AlertDialogHeader>
              <AlertDialogBody>
                {pendingStatusTask.status === 'DA_INIZIARE'
                  ? `Sei sicuro di voler avviare il intervento "${pendingStatusTask.title}"?`
                  : `Sei sicuro di voler completare il intervento "${pendingStatusTask.title}"?`}
              </AlertDialogBody>
              <AlertDialogFooter>
                <Button ref={statusAlertCancelRef} onClick={onStatusClose}>Annulla</Button>
                <Button
                  backgroundColor="#688CC8"
                  color="white"
                  onClick={handleStatusConfirm}
                  ml={3}
                  _hover={{ bg: 'gray.700' }}
                  _active={{ bg: 'gray.600' }}
                  _focus={{ boxShadow: 'none' }}
                >
                  Conferma
                </Button>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialogOverlay>
        </AlertDialog>
      )}

      {/* ── MODAL: GRID EDIT ───────────────────────────────────────────────── */}
      {gridModalState && (
        <div
          style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.52)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          onClick={closeGridModal}
        >
          <div
            style={{ background: 'white', borderRadius: '14px', padding: '28px 30px', width: '580px', maxWidth: '95vw', boxShadow: '0 24px 60px rgba(0,0,0,0.22)', animation: 'fadeUp 0.18s ease' }}
            onClick={e => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px' }}>
              <div>
                <div style={{ fontSize: '16px', fontWeight: 700, color: '#5f84ca' }}>
                  {getGridModalTitle()}
                </div>
              </div>
              <button onClick={closeGridModal} style={{ fontSize: '22px', color: '#94a3b8', background: 'none', border: 'none', cursor: 'pointer', lineHeight: 1, paddingLeft: '16px' }}>
                ×
              </button>
            </div>
            <textarea
              value={gridModalValue}
              onChange={e => setGridModalValue(e.target.value)}
              autoFocus
              style={{
                width: '100%',
                height: '320px',
                border: '1.5px solid #dde5f0',
                borderRadius: '8px',
                padding: '13px 14px',
                fontFamily: "'Inter', system-ui, -apple-system, sans-serif",
                fontSize: '13px',
                lineHeight: '1.7',
                color: '#1a2942',
                background: '#fafbfd',
                resize: 'vertical',
                outline: 'none',
                boxSizing: 'border-box',
                display: 'block',
              }}
            />
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '16px' }}>
              <button onClick={closeGridModal} style={{ border: '1.5px solid #dde5f0', color: '#64748b', background: 'white', padding: '9px 22px', borderRadius: '8px', fontSize: '13.5px', cursor: 'pointer', fontFamily: "'Inter', system-ui, sans-serif" }}>
                Annulla
              </button>
              <button onClick={handleSaveNote} style={{ background: '#5f84ca', color: 'white', border: 'none', padding: '9px 22px', borderRadius: '8px', fontSize: '13.5px', fontWeight: 600, cursor: 'pointer', fontFamily: "'Inter', system-ui, sans-serif" }}>
                Salva
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(-6px); }
          to   { opacity: 1; transform: translateY(0);    }
        }
        ::-webkit-scrollbar        { width: 5px; height: 5px; }
        ::-webkit-scrollbar-thumb  { background: #c8d5e0; border-radius: 3px; }
        ::-webkit-scrollbar-track  { background: transparent; }
        :fullscreen {
          background: white;
          display: flex;
          flex-direction: column;
          padding: 8px 8px 40px;
          overflow: auto;
          height: 100%;
        }
        :fullscreen .print-weekly-grid-container {
          padding: 0 8px 0 0;
          overflow-x: auto;
          /* This child sets overflow-x, so per spec its automatic min-height becomes 0
             instead of content-based — inside the fullscreen flex column it would then
             shrink to fit and clip the bottom rows (Ferie) instead of the intended
             behaviour of the :fullscreen ancestor scrolling. flex-shrink:0 keeps it at
             its natural content height so that ancestor's overflow:auto does the scrolling. */
          flex-shrink: 0;
        }
        :fullscreen .print-weekly-grid {
          min-width: 100%;
        }
        /* Altezza minima di default (solo l'attività cresce se il contenuto lo
           richiede — le altre righe restano alla loro altezza naturale). Esclude la
           riga ridimensionata a mano con la maniglia di trascinamento — altrimenti
           questo min-height la riporterebbe sempre alla stessa altezza a schermo intero. */
        :fullscreen .weekly-grid-activity-cell:not(.weekly-grid-activity-cell--custom-height) {
          /* the cell also carries an inline minHeight:300px (React style prop),
             which always beats a plain class rule in the cascade regardless of
             ordering — !important is the only way to actually override it here */
          min-height: 65vh !important;
        }
        .weekly-grid-row-handle:hover {
          background: rgba(95,132,202,0.2) !important;
        }
        .weekly-grid-col-handle:hover {
          background: rgba(95,132,202,0.2) !important;
        }
      `}</style>
    </Box>
  );
}
